<template>
  <form-page-template :variant="formPageVariant">
    <div
      class="uc-form"
      :class="{
        'uc-form--borna-auth': isBornaAuthCard,
        'uc-form--design-01': isDesign01,
        'uc-form--design-02': isDesign02,
        'uc-form--design-03': isDesign03,
        'uc-form--student-onboarding': isStudentOnboardingState,
      }"
    >
      <!--
        DESIGN-01 (login landing). The brand lockup and the eyebrow sit ABOVE
        the shared per-state <h1>, so they are rendered here rather than inside
        the PHONE_ENTRY form. Everything is gated on `isDesign01` (mobile flow
        on, email fallback not showing, state === PHONE_ENTRY) so DESIGN-02/03
        and the email-fallback screen render exactly as before. Real supplied
        assets -- not traced, not inlined.

        DOM order is [Borna, divider, foundation]: the card is RTL for fa (the
        design locale), so the first child lands on the visual right, matching
        the reference.
      -->
      <div
        v-if="isDesign01"
        class="design01-lockup"
        data-testid="design01-lockup"
      >
        <span class="design01-lockup__mark">
          <img
            class="design01-lockup__borna"
            src="@/assets/logo/borna-identity.png"
            alt="برنا"
          />
          <small class="auth-lockup__tagline">
            {{ $t('login.mobile.brandTagline') }}
          </small>
        </span>
        <span class="design01-lockup__divider" aria-hidden="true"></span>
        <span class="design01-lockup__foundation">
          <span class="design01-lockup__powered-by">{{
            $t('login.mobile.poweredBy')
          }}</span>
          <img
            class="design01-lockup__foundation-mark"
            src="@/assets/logo/foundation-logo.png"
            :alt="$t('login.mobile.poweredBy')"
          />
        </span>
      </div>
      <p
        v-if="isDesign01"
        class="design01-eyebrow"
        data-testid="design01-eyebrow"
      >
        <!--
          WP-04V-B round 2: this was the `✨` emoji, which the platform renders
          as a multicolour glyph beside a one-colour indigo line. The prototype
          draws a 15px indigo stroke mark; the shape is copied from the pinned
          prototype, same precedent as the `language` / `logout` rows.
        -->
        <svg
          class="design01-eyebrow__spark"
          viewBox="0 0 24 24"
          width="15"
          height="15"
          fill="none"
          stroke="currentColor"
          stroke-width="1.8"
          stroke-linecap="round"
          stroke-linejoin="round"
          aria-hidden="true"
          focusable="false"
        >
          <path d="m12 2 1.6 5.4L19 9l-5.4 1.6L12 16l-1.6-5.4L5 9l5.4-1.6Z" />
          <path d="m19 16 .8 2.2L22 19l-2.2.8L19 22l-.8-2.2L16 19l2.2-.8Z" />
        </svg>
        {{ $t('login.mobile.brandEyebrow') }}
      </p>

      <!--
        DESIGN-02 (OTP) in-card composition. Rendered ABOVE the shared
        per-state <h1> so the visual order matches the prototype:
        Borna-only lockup -> change-number link -> eyebrow -> heading.
        Everything is gated on `isDesign02`, so DESIGN-01, DESIGN-03 and the
        email-fallback screen render exactly as before.

        - No foundation mark here: the prototype's DESIGN-02 shows the Borna
          wordmark alone (foundation lockup is DESIGN-01 only).
        - The change-number control is the SAME `resetMobileFlow` action that
          used to sit under the primary button -- only its position, icon and
          label changed. No new navigation or state behaviour.
      -->
      <template v-if="isDesign02">
        <div class="auth-lockup design02-lockup" data-testid="design02-lockup">
          <img
            class="auth-lockup__borna design02-lockup__borna"
            src="@/assets/logo/borna-identity.png"
            alt="برنا"
          />
          <small class="auth-lockup__tagline">
            {{ $t('login.mobile.brandTagline') }}
          </small>
        </div>
        <button
          type="button"
          class="auth-back design02-back"
          data-testid="back-to-phone-button"
          :disabled="isSubmitting"
          @click="resetMobileFlow"
        >
          <svg
            class="design02-back__arrow"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M19 12H5m7-7l-7 7 7 7"
              fill="none"
              stroke="currentColor"
              stroke-width="2"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {{ $t('login.mobile.editNumber') }}
        </button>
        <p class="auth-eyebrow design02-eyebrow" data-testid="design02-eyebrow">
          {{ $t('login.mobile.otpEyebrow') }}
        </p>
      </template>

      <!--
        DESIGN-03 (role selection) in-card composition — WP-04V-B round 2.

        The canonical prototype composes this screen exactly as it composes the
        OTP screen: Borna-only lockup -> hairline -> back link -> eyebrow ->
        heading -> lead -> choice cards. It shares `.auth-lockup` / `.auth-back`
        / `.auth-eyebrow` with DESIGN-02 rather than restating the same pixels,
        and it keeps its own testids.

        Behaviour is untouched. The back control is the SAME `resetMobileFlow`
        action and the SAME `registration-required-back-button` testid that used
        to sit under the choice cards -- only its position, icon and label
        changed, exactly as DESIGN-02 did for «اصلاح شماره».
      -->
      <template v-if="isDesign03">
        <div class="auth-lockup" data-testid="design03-lockup">
          <img
            class="auth-lockup__borna"
            src="@/assets/logo/borna-identity.png"
            alt="برنا"
          />
          <small class="auth-lockup__tagline">
            {{ $t('login.mobile.brandTagline') }}
          </small>
        </div>
        <button
          type="button"
          class="auth-back"
          data-testid="registration-required-back-button"
          :disabled="isSubmitting"
          @click="resetMobileFlow"
        >
          <svg
            class="auth-back__arrow"
            viewBox="0 0 24 24"
            width="18"
            height="18"
            aria-hidden="true"
            focusable="false"
          >
            <path
              d="M19 12H5m7-7l-7 7 7 7"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
            />
          </svg>
          {{ $t('login.mobile.registrationRequired.backToLogin') }}
        </button>
        <p class="auth-eyebrow" data-testid="design03-eyebrow">
          {{ $t('login.mobile.registrationRequired.eyebrow') }}
        </p>
      </template>

      <!--
        Task 12 / STEP-02 (A). One heading per card, derived from the state the
        user is actually in. The static "welcome back" copy previously stayed on
        screen through OTP entry, role choice and registration, so a brand-new
        user creating their first account was greeted as a returning one, and
        every later step rendered an <h2> underneath a stale <h1>. The per-state
        <h2>s are gone: this IS that heading now.
      -->
      <!--
        TASK-SF-07: the onboarding wizard owns a distinct <h1> per step
        (DESIGN-04/05/09), so this shared card heading is suppressed while it
        is mounted rather than rendering a second, stale heading above it.
      -->
      <h1 v-if="mobileState !== 'STUDENT_ONBOARDING'" class="uc-form-header">
        {{ pageHeading }}
      </h1>

      <!--
        WP-04V-B round 2 — presentation only, no message is added or removed.

        The condition used to be `error || $route.query['401'] === 'true'`.
        `error` is read ONCE, in `data()`, so when the app's global 401 handler
        appends `?401=true` to the URL *after* mount (which it does when any
        auth call returns 401 -- e.g. a wrong OTP code), the query became true
        while `error` stayed undefined: an EMPTY red box appeared and then
        followed the user through every later state of the journey. Rendering
        on `error` alone drops the empty box and still shows the real
        session-expired message, which `data()` sets from the same query on a
        normal expired-session entry. The 401 redirect itself is untouched.
      -->
      <div v-if="error" class="alert alert-danger" role="alert">
        {{ error }}
      </div>

      <!--
        Task 11 / STEP-08. Mobile-first entry (FR-01, FR-13, Q-007). All
        state below (mobileState, challengeId, ownershipGrantId,
        registrationGrantId) is plain Vue component data -- never a route,
        never localStorage. A refresh destroys the Vue instance and remounts
        at PHONE_ENTRY by construction; there is no URL for any later state,
        so "direct navigation to OTP/resolve/login" is not reachable at all
        (SR-21, section 15/19 of the STEP-08 authorization).
      -->
      <div
        v-if="mobileFlowEnabled && !showEmailLogin"
        data-testid="mobile-login"
      >
        <form
          v-if="mobileState === 'PHONE_ENTRY'"
          @submit.prevent="sendMobileCode"
          data-testid="phone-entry-form"
        >
          <!--
            AUTH-G1 (02_* §15.11): the link-required flow reuses this exact
            form -- only the lead copy differs, explaining WHY a phone
            number is being asked for here instead of the usual tutoring
            pitch.
          -->
          <p class="uc-form-lead">
            {{
              isGoogleLinkFlow
                ? $t('login.mobile.google.linkRequired.lead')
                : $t('login.mobile.phoneEntryLead')
            }}
          </p>

          <div class="uc-form-element">
            <!--
              WP-04V-B: the rendered prototype's `.field-label` carries a
              required star. The field already IS required (submit is blocked
              without a number), so this states existing behaviour -- it adds
              no validation and changes no payload.
            -->
            <label for="mobilePhoneInput_phone_number" class="uc-form-label"
              >{{ $t('login.mobile.phoneLabel')
              }}<b class="design01-required-star" aria-hidden="true"
                >*</b
              ></label
            >
            <!--
              DESIGN-01 / HUMAN DECISION 1: phone identity is Iran-only. The
              country selector is removed; a fixed, non-interactive +۹۸ chip
              states the identity. The field accepts a local Iranian number in
              any convenient form (0912…, 912…, spaced, Persian or Latin
              digits); `onPhoneInput` normalises it to a Latin-digit E.164
              (+98…) via awesome-phonenumber before anything is sent, so the
              backend contract is unchanged -- `sendMobileChallenge` still
              receives exactly the +98 E.164 string it received from
              vue-phone-number-input's `e164`. A bare 0912… or a Persian-digit
              string can never reach the API.

              The wrapper keeps the `phone-input` class the RTL block at the
              bottom of this file targets, so the digit run stays LTR inside the
              RTL card.
            -->
            <div
              class="phone-input design01-phone"
              data-testid="mobile-phone-input"
            >
              <span class="design01-phone__prefix" aria-hidden="true">{{
                $t('login.mobile.phonePrefix')
              }}</span>
              <input
                id="mobilePhoneInput_phone_number"
                ref="mobilePhoneInput"
                v-model.trim="phone"
                class="design01-phone__field"
                type="tel"
                inputmode="tel"
                autocomplete="tel"
                :placeholder="$t('login.mobile.phonePlaceholder')"
                :aria-invalid="mobileError ? 'true' : null"
                :aria-describedby="mobileError ? 'mobileAuthError' : null"
                @beforeinput="onPhoneBeforeInput"
                @paste="onPhonePaste"
                @input="onPhoneInput"
              />
            </div>
          </div>

          <button
            class="uc-form-button design01-cta"
            type="submit"
            data-testid="send-code-button"
            :disabled="!isValidPhone || isSubmitting"
          >
            {{ $t('login.mobile.sendCodeButton') }}
            <svg
              class="design01-cta__arrow"
              viewBox="0 0 24 24"
              width="20"
              height="20"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M14 5l-7 7 7 7"
                fill="none"
                stroke="currentColor"
                stroke-width="2"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
          </button>

          <!--
            DESIGN-01: the «یا» divider, the Google entry point and the security
            note belong to this screen only, so they live inside the PHONE_ENTRY
            form rather than in the shared card body.

            AUTH-G1 (02_* §15.11): suppressed during the Google-link flow --
            offering "Continue with Google" again while already resolving a
            Google identity would be confusing, and every AUTH-G1 CASE only
            reaches PHONE_ENTRY through that flow already.
          -->
          <div
            v-if="!isGoogleLinkFlow"
            class="design01-divider"
            aria-hidden="true"
          >
            <span>{{ $t('login.mobile.orDivider') }}</span>
          </div>

          <!--
            AUTH-G1 (00_* §16, 02_* §15.9): a new entry point to the unified
            Google resolver (`GET /auth/google`). type="button" so it never
            submits the phone form. The resolver decides login vs. create-
            account vs. secure linking -- nothing here does.
          -->
          <button
            v-if="!isGoogleLinkFlow"
            type="button"
            class="uc-form-button google design01-google"
            data-testid="google-signin-button"
            @click="signInWithGoogle"
          >
            <google-logo class="design01-google__icon" />
            {{ $t('login.mobile.googleButton') }}
          </button>

          <p class="design01-secure" data-testid="design01-secure-note">
            <svg
              class="design01-secure__icon"
              viewBox="0 0 24 24"
              width="16"
              height="16"
              aria-hidden="true"
              focusable="false"
            >
              <path
                d="M12 3l7 3v5c0 4.4-3 8.3-7 9-4-.7-7-4.6-7-9V6l7-3z"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linejoin="round"
              />
              <path
                d="M9 12l2 2 4-4"
                fill="none"
                stroke="currentColor"
                stroke-width="1.6"
                stroke-linecap="round"
                stroke-linejoin="round"
              />
            </svg>
            {{ $t('login.mobile.secureNote') }}
          </p>
        </form>

        <form
          v-else-if="mobileState === 'OTP_ENTRY'"
          @submit.prevent="confirmMobileCode"
          data-testid="otp-entry-form"
        >
          <!--
            DESIGN-02: one informal centred sentence with the destination
            number folded in and MASKED (see `maskedPhone`). `{0}` is the
            masked digit run, kept LTR inside the RTL card -- same problem the
            `.phone-inline` / RTL block already solves elsewhere. The raw
            national number is never rendered on this screen.
          -->
          <i18n
            path="login.mobile.otpSentToSentence"
            tag="p"
            class="uc-form-lead"
          >
            <span class="phone-inline" dir="ltr" data-testid="otp-sent-to">{{
              maskedPhone
            }}</span>
          </i18n>

          <!--
            Task 12 / STEP-03 (section 6). The SimpleOtpInput already used by
            the signup verification step -- six-box entry, paste handling and
            WebOTP auto-fill. `otpCode` remains exactly the value
            confirmMobileCode sends -- no payload or state-machine change.

            DESIGN-02: the visible field label is dropped (the lead sentence
            names the task); the group keeps an accessible name via
            `aria-label`. The boxes are re-skinned to the prototype's indigo
            geometry by scoped `::v-deep` rules -- `_otpInput.scss` (signup /
            verification) is untouched.
          -->
          <div
            class="uc-form-element design02-otp-group"
            role="group"
            :aria-label="$t('login.mobile.otpLabel')"
            :aria-describedby="mobileError ? 'mobileAuthError' : null"
          >
            <simple-otp-input
              ref="mobileOtpInput"
              v-model="otpCode"
              data-testid="mobile-otp-input"
              :length="6"
              :paste-delay-ms="192"
              :input-props="otpInputProps"
              with-web-otp
            />
          </div>

          <!--
            DESIGN-02: the primary CTA follows the OTP boxes directly (prototype
            order: boxes -> CTA -> resend). Handler, payload, the six-digit
            `:disabled` gate and the confirm flow are unchanged.
          -->
          <button
            class="uc-form-button design02-cta"
            type="submit"
            data-testid="confirm-code-button"
            :disabled="!isValidOtp || isSubmitting"
          >
            {{ $t('login.mobile.confirmButton') }}
          </button>

          <!--
            Task 12 / STEP-03 (section 8/9). Resend without losing the number
            and without restarting the flow. The cooldown is a CLIENT-SIDE
            throttle only: the backend sends no Retry-After and no OTP expiry.
            DESIGN-02 shows it as a compact "resend code · mm:ss" suffix --
            deliberately NOT phrased as a code-expiry or a server unlock time.
            It sits below the primary CTA, matching the prototype.
          -->
          <p class="otp-resend design02-resend">
            <button
              type="button"
              class="uc-link-button design02-resend__button"
              data-testid="resend-code-button"
              :disabled="!canResendCode"
              @click="resendMobileCode"
            >
              {{ $t('login.mobile.resendCta')
              }}<span
                v-if="resendCooldown > 0"
                class="design02-resend__timer"
                data-testid="resend-cooldown"
              >
                &middot; {{ resendCountdownLabel }}</span
              >
            </button>
          </p>

          <p
            v-if="resendNotice"
            class="otp-resend__notice design02-resend__notice"
            role="status"
            data-testid="resend-notice"
          >
            {{ resendNotice }}
          </p>
        </form>

        <div
          v-else-if="
            mobileState === 'RESOLVING' ||
              mobileState === 'SESSION_ESTABLISHING'
          "
          class="uc-form-pending"
          data-testid="mobile-pending"
          role="status"
        >
          <!--
            Task 12 / STEP-02 (E): the pending state was text-only. It is
            announced correctly but had no visible progress affordance, so the
            card looked frozen between OTP confirmation and the dashboard.
            Presentational only -- aria-hidden so nothing new is announced.
          -->
          <span class="uc-form-spinner" aria-hidden="true"></span>
          <p class="uc-form-lead">
            {{ $t('login.mobile.establishingSession') }}
          </p>
        </div>

        <!--
          Task 11 new-user registration. STEP-08 shipped this state as a
          controlled "not available yet" terminal because STEP-09/10/11 did
          not exist; they do now, so the terminal message is gone and the
          verified registrationGrantId drives real registration.

          /login carries no role intent, so the FIRST thing a new user picks is
          which account to create. This is initial-role selection -- not the
          STEP-12 acting-role switch, which only ever applies to an existing
          authenticated identity that already holds both roles.
        -->
        <div
          v-else-if="mobileState === 'REGISTRATION_REQUIRED'"
          data-testid="registration-required"
        >
          <p class="uc-form-lead">
            {{ $t('login.mobile.registrationRequired.body') }}
          </p>

          <!--
            Task 12 / STEP-02 (section 6). This screen IS the choice, so there
            is no separate primary CTA to rank the two options against. They
            were previously two identical full-width primary pills with nothing
            to tell them apart but their labels; they are now equal-weight
            choice cards carrying a one-line description each. Deliberately not
            "Student primary, Volunteer secondary" -- that would imply a default
            the product does not actually have.

            Behaviour is untouched: same handlers, same testids, same states.
          -->
          <!--
            WP-04V-B round 2. The canonical prototype's `.role-entry`: a 20px
            card with a 54x54 tinted icon tile, a 15/700 title over an 11px
            muted line, and a 30x30 arrow tile. The two roles are tinted
            differently there (indigo / teal) and are still equal weight --
            neither is styled as the primary action, because this screen IS
            the choice.

            The glyphs are copied from the pinned prototype (same precedent as
            the `language` / `logout` rows in WP-04V-E), not redrawn.
          -->
          <div class="role-choice-group">
            <button
              type="button"
              class="role-choice role-choice--student"
              data-testid="register-as-student"
              @click="chooseRegistrationRole('student')"
            >
              <span class="role-choice__icon" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  width="28"
                  height="28"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  focusable="false"
                >
                  <path
                    d="M4 5.5A3.5 3.5 0 0 1 7.5 2H12v18H7.5A3.5 3.5 0 0 0 4 23Z"
                  />
                  <path
                    d="M20 5.5A3.5 3.5 0 0 0 16.5 2H12v18h4.5a3.5 3.5 0 0 1 3.5 3Z"
                  />
                </svg>
              </span>
              <span class="role-choice__content">
                <strong class="role-choice__label">
                  {{ $t('login.mobile.registrationRequired.student') }}
                </strong>
                <small class="role-choice__hint">
                  {{ $t('login.mobile.registrationRequired.studentHint') }}
                </small>
              </span>
              <span class="role-choice__arrow" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  width="19"
                  height="19"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  focusable="false"
                >
                  <path d="M19 12H5" />
                  <path d="m11 6-6 6 6 6" />
                </svg>
              </span>
            </button>
            <!-- P42-04: while teacher registration is Coming Soon the card is
                 DISABLED -- a real `disabled` button (no click, out of the
                 tab order, inert to Enter/Space), muted, with the badge and
                 the "coming soon" hint it already had. This is a UI state,
                 not an access control: the registration endpoints are
                 unchanged (accepted limitation, tracked separately). -->
            <button
              type="button"
              class="role-choice role-choice--volunteer"
              :class="{ 'role-choice--disabled': !teacherExperienceAvailable }"
              :disabled="!teacherExperienceAvailable"
              :aria-disabled="!teacherExperienceAvailable ? 'true' : null"
              :aria-describedby="
                !teacherExperienceAvailable
                  ? 'register-as-volunteer-hint'
                  : null
              "
              data-testid="register-as-volunteer"
              @click="chooseRegistrationRole('volunteer')"
            >
              <span class="role-choice__icon" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  width="28"
                  height="28"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  focusable="false"
                >
                  <circle cx="9" cy="8" r="4" />
                  <path d="M2 21a7 7 0 0 1 14 0M17 8h5M19.5 5.5v5" />
                </svg>
              </span>
              <span class="role-choice__content">
                <strong class="role-choice__label">
                  {{ $t('login.mobile.registrationRequired.volunteer') }}
                  <!-- SX-02: badged while teacher registration is Coming Soon -->
                  <span
                    v-if="!teacherExperienceAvailable"
                    class="role-choice__badge"
                    data-testid="register-as-volunteer-coming-soon"
                    >{{ $t('studentComingSoon.badge') }}</span
                  >
                </strong>
                <small
                  id="register-as-volunteer-hint"
                  class="role-choice__hint"
                >
                  {{
                    teacherExperienceAvailable
                      ? $t('login.mobile.registrationRequired.volunteerHint')
                      : $t(
                          'login.mobile.registrationRequired.volunteerHintComingSoon'
                        )
                  }}
                </small>
              </span>
              <span class="role-choice__arrow" aria-hidden="true">
                <svg
                  viewBox="0 0 24 24"
                  width="19"
                  height="19"
                  fill="none"
                  stroke="currentColor"
                  stroke-width="1.8"
                  stroke-linecap="round"
                  stroke-linejoin="round"
                  focusable="false"
                >
                  <path d="M19 12H5" />
                  <path d="m11 6-6 6 6 6" />
                </svg>
              </span>
            </button>
          </div>

          <!--
            The prototype's reassurance line under the choice. It states a
            capability the implementation already has -- a second role is
            attached to the SAME account through ROLE_ONBOARDING_REQUIRED, never
            by creating a second identity -- so it is reported, not invented.
          -->
          <p class="role-choice-note" data-testid="role-choice-note">
            <svg
              class="role-choice-note__icon"
              viewBox="0 0 24 24"
              width="17"
              height="17"
              fill="none"
              stroke="currentColor"
              stroke-width="1.8"
              stroke-linecap="round"
              stroke-linejoin="round"
              aria-hidden="true"
              focusable="false"
            >
              <path d="M12 3 20 6v6c0 5-3.4 8.3-8 9-4.6-.7-8-4-8-9V6Z" />
              <path d="m9 12 2 2 4-4" />
            </svg>
            {{
              teacherExperienceAvailable
                ? $t('login.mobile.registrationRequired.roleNote')
                : $t('login.mobile.registrationRequired.roleNoteComingSoon')
            }}
          </p>
        </div>

        <!--
          TASK-SF-07: the redesigned STUDENT journey. Entered only from the
          student role card (or a preselected student roleIntent), never from
          the volunteer card. The wizard is a child component; the terminal
          call is still the existing AuthService.registerMobileStudent, and
          the verified registrationGrantId is the sole identity authority.
        -->
        <student-onboarding-wizard
          v-else-if="mobileState === 'STUDENT_ONBOARDING'"
          :grant-id="registrationGrantId"
          :email-error="onboardingEmailError"
          :submitting="isRegistering"
          @back-to-role="mobileState = 'REGISTRATION_REQUIRED'"
          @clear-email-conflict="onboardingEmailError = null"
          @submit="submitStudentOnboarding"
        />

        <!--
          The Task-11 mobile registration form. Deliberately NOT the legacy
          /sign-up form: no password, no username, and the phone is shown
          read-only for orientation only -- it is never submitted, because the
          backend re-reads the verified number from the grant.
        -->
        <form
          v-else-if="mobileState === 'REGISTRATION_FORM'"
          data-testid="registration-form"
          @submit.prevent="submitMobileRegistration"
        >
          <!--
            Task 12 / STEP-02 (B). These three fields carried `uc-form-input`,
            which _form.scss marks "OLD STYLES" -- a 30px underlined
            content-box input. Every other field in this flow uses
            `uc-form-text-input`, so the design language visibly changed the
            moment a new user reached registration. Class only: no change to
            v-model bindings, field names, validation, or the submitted body.
          -->
          <!--
            Task 12 / STEP-05 (section 10). The form opened straight into
            fields with nothing saying what was being created or what was
            actually needed. Role-specific, so a user who expressed intent at
            phone entry can confirm the flow honoured it.
          -->
          <p class="uc-form-lead" data-testid="registration-lead">
            {{ registrationLead }}
          </p>

          <p v-if="phoneDisplay" class="uc-form-subtext">
            {{ $t('login.mobile.registrationForm.phoneLabel') }}
          </p>
          <p
            v-if="phoneDisplay"
            class="phone-inline"
            data-testid="registration-phone-readonly"
          >
            {{ phoneDisplay }}
          </p>

          <div class="uc-form-element">
            <label class="uc-form-label" for="registration-first-name">
              {{ $t('login.mobile.registrationForm.firstName') }}
              <span class="uc-form-required">{{
                $t('login.mobile.registrationForm.requiredHint')
              }}</span>
            </label>
            <input
              id="registration-first-name"
              v-model="registration.firstName"
              class="uc-form-text-input"
              type="text"
              required
              data-testid="registration-first-name"
            />
          </div>

          <div class="uc-form-element">
            <label class="uc-form-label" for="registration-last-name">
              {{ $t('login.mobile.registrationForm.lastName') }}
              <span class="uc-form-required">{{
                $t('login.mobile.registrationForm.requiredHint')
              }}</span>
            </label>
            <input
              id="registration-last-name"
              v-model="registration.lastName"
              class="uc-form-text-input"
              type="text"
              required
              data-testid="registration-last-name"
            />
          </div>

          <!-- Q-006: email is OPTIONAL for an OTP-first account. -->
          <div class="uc-form-element">
            <label class="uc-form-label" for="registration-email">
              {{ $t('login.mobile.registrationForm.emailOptional') }}
            </label>
            <input
              id="registration-email"
              v-model="registration.email"
              class="uc-form-text-input"
              type="email"
              data-testid="registration-email"
            />
          </div>

          <label class="uc-form-checkbox">
            <input
              v-model="registration.terms"
              type="checkbox"
              data-testid="registration-terms"
            />
            <span>{{ $t('login.mobile.registrationForm.terms') }}</span>
          </label>
          <!-- Sibling, not a child of the label -- see the note on the role
               onboarding terms link below. -->
          <i18n path="login.mobile.termsLegal" tag="p" class="uc-form-subtext">
            <router-link
              :to="`/${$i18n.locale}/legal`"
              target="_blank"
              class="uc-link"
              data-testid="registration-legal-link"
              >{{ $t('studentPartnerSignUp.agreementLink') }}</router-link
            >
          </i18n>

          <button
            type="submit"
            class="uc-form-button"
            data-testid="registration-submit"
            :disabled="!canSubmitRegistration"
          >
            {{ $t('login.mobile.registrationForm.submit') }}
          </button>
          <p class="uc-form-secondary-action">
            <button
              type="button"
              class="uc-link-button"
              data-testid="registration-form-back-button"
              @click="mobileState = 'REGISTRATION_REQUIRED'"
            >
              {{ $t('login.mobile.registrationForm.back') }}
            </button>
          </p>
        </form>

        <!--
          FR-07, SR-09: generic, discloses no account count/identity/role,
          never retried automatically.
        -->
        <div
          v-else-if="mobileState === 'ACCOUNT_CONFLICT'"
          data-testid="account-conflict"
        >
          <p class="uc-form-lead">
            {{ $t('login.mobile.accountConflict.body') }}
          </p>
          <button
            type="button"
            class="uc-form-button"
            data-testid="account-conflict-back-button"
            @click="resetMobileFlow"
          >
            {{ $t('login.mobile.backToPhone') }}
          </button>
        </div>

        <!--
          AUTH-G1 (02_* §15.4): the pending Google identity is being fetched
          server-side before the create-account screen can render. Reuses
          the exact spinner pattern the RESOLVING/SESSION_ESTABLISHING state
          above already uses.
        -->
        <div
          v-else-if="mobileState === 'GOOGLE_CREATE_ACCOUNT_LOADING'"
          class="uc-form-pending"
          data-testid="google-create-account-loading"
          role="status"
        >
          <span class="uc-form-spinner" aria-hidden="true"></span>
          <p class="uc-form-lead">
            {{ $t('login.mobile.establishingSession') }}
          </p>
        </div>

        <!--
          AUTH-G1 (00_* AG-04, 02_* §15.4): a brand-new Google identity, no
          existing candidate account. Explicit consent (checkbox + legal
          link) mirrors ROLE_ONBOARDING_REQUIRED's established pattern
          below, rather than inventing a new consent affordance -- the
          checkbox is a sibling of the label, never a child, for the same
          reason (reading the agreement must not silently accept it).
          Name fields are shown only when Google supplied neither; email is
          shown read-only when Google verified one (never editable here --
          it was never collected from the user at all).
        -->
        <form
          v-else-if="mobileState === 'GOOGLE_CREATE_ACCOUNT'"
          data-testid="google-create-account"
          @submit.prevent="confirmGoogleAccountCreation"
        >
          <p class="uc-form-lead">
            {{ $t('login.mobile.google.createAccount.lead') }}
          </p>
          <p v-if="googlePendingEmail" class="uc-form-subtext">
            {{ googlePendingEmail }}
          </p>
          <template v-if="!googlePendingFirstName">
            <div class="uc-form-element">
              <label for="googleAccountFirstName" class="uc-form-label">{{
                $t('login.studentOnboarding.step1.firstNameLabel')
              }}</label>
              <input
                id="googleAccountFirstName"
                v-model.trim="googleAccountFirstName"
                class="uc-form-input"
                type="text"
                data-testid="google-account-first-name"
              />
            </div>
            <div class="uc-form-element">
              <label for="googleAccountLastName" class="uc-form-label">{{
                $t('login.studentOnboarding.step1.lastNameLabel')
              }}</label>
              <input
                id="googleAccountLastName"
                v-model.trim="googleAccountLastName"
                class="uc-form-input"
                type="text"
                data-testid="google-account-last-name"
              />
            </div>
          </template>
          <label class="uc-form-checkbox">
            <input
              v-model="googleAccountTerms"
              type="checkbox"
              data-testid="google-account-terms"
            />
            <span>{{ $t('login.mobile.google.createAccount.terms') }}</span>
          </label>
          <i18n path="login.mobile.termsLegal" tag="p" class="uc-form-subtext">
            <router-link
              :to="`/${$i18n.locale}/legal`"
              target="_blank"
              class="uc-link"
              data-testid="google-account-legal-link"
              >{{ $t('studentPartnerSignUp.agreementLink') }}</router-link
            >
          </i18n>
          <button
            type="submit"
            class="uc-form-button"
            data-testid="google-account-submit"
            :disabled="
              !googleAccountTerms ||
                isConfirmingGoogleAccount ||
                (!googlePendingFirstName &&
                  (!googleAccountFirstName.trim() ||
                    !googleAccountLastName.trim()))
            "
          >
            {{ $t('login.mobile.google.createAccount.submit') }}
          </button>
          <p class="uc-form-secondary-action">
            <button
              type="button"
              class="uc-link-button"
              data-testid="google-account-cancel-button"
              :disabled="isConfirmingGoogleAccount"
              @click="resetMobileFlow"
            >
              {{ $t('login.mobile.backToPhone') }}
            </button>
          </p>
        </form>

        <!--
          AUTH-G1 (00_* AG-05, 02_* §15.6): no accepted ownership-proof
          mechanism exists for any candidate account -- never a doomed OTP
          prompt, never a silent auto-link, never a duplicate account. The
          only forward path is support: Batch 45, the native Synova button on
          this screen, so no second entry here.
        -->
        <div
          v-else-if="mobileState === 'GOOGLE_LINK_UNAVAILABLE'"
          data-testid="google-link-unavailable"
        >
          <p class="uc-form-lead">
            {{ $t('login.mobile.google.linkUnavailable.body') }}
          </p>
          <p class="uc-form-secondary-action">
            <button
              type="button"
              class="uc-link-button"
              data-testid="google-link-unavailable-back-button"
              @click="resetMobileFlow"
            >
              {{ $t('login.mobile.backToPhone') }}
            </button>
          </p>
        </div>

        <!--
          Task 11 / STEP-12 (Q-011): the requested role is missing from this
          ALREADY AUTHENTICATED account. The session established a moment ago
          is untouched -- this offers to add the role to the same identity.
          Deliberately not a link to /sign-up and deliberately no second OTP.
        -->
        <form
          v-else-if="mobileState === 'ROLE_ONBOARDING_REQUIRED'"
          data-testid="role-onboarding-required"
          @submit.prevent="submitRoleOnboarding"
        >
          <p class="uc-form-lead">
            {{ $t('login.mobile.roleOnboardingRequired.body') }}
          </p>
          <label class="uc-form-checkbox">
            <input
              v-model="roleOnboardingTerms"
              type="checkbox"
              data-testid="role-onboarding-terms"
            />
            <span>{{ $t('login.mobile.roleOnboardingRequired.terms') }}</span>
          </label>
          <!--
            Task 12 / STEP-05 (section 11). The legal destination is the app's
            OWN existing /:lang/legal route, linked the way FormPageTemplate
            already links it. Deliberately a SIBLING of the label rather than a
            child: an anchor inside a <label> toggles the checkbox on click, so
            nesting it would make reading the agreement silently accept it.
          -->
          <i18n path="login.mobile.termsLegal" tag="p" class="uc-form-subtext">
            <router-link
              :to="`/${$i18n.locale}/legal`"
              target="_blank"
              class="uc-link"
              data-testid="role-onboarding-legal-link"
              >{{ $t('studentPartnerSignUp.agreementLink') }}</router-link
            >
          </i18n>
          <button
            type="submit"
            class="uc-form-button"
            data-testid="role-onboarding-submit"
            :disabled="!roleOnboardingTerms || isOnboardingRole"
          >
            {{ roleOnboardingSubmitLabel }}
          </button>
          <!--
            Task 12 / STEP-05 (section 8). Without this the screen was a trap:
            the only way forward was accepting a role the user may have reached
            by mistake, and the only way out was closing the tab -- on an
            account that is ALREADY signed in.

            Subordinate to the primary CTA on purpose, and it declines a role
            rather than the login: no logout, no role mutation, no server call.
            It drops the pending intent and hands over to the dashboard, where
            STEP-04's rules decide the acting role.
          -->
          <p class="uc-form-secondary-action">
            <button
              type="button"
              class="uc-link-button"
              data-testid="role-onboarding-skip-button"
              :disabled="isOnboardingRole"
              @click="continueWithCurrentAccount"
            >
              {{ $t('login.mobile.roleOnboardingRequired.skip') }}
            </button>
          </p>
        </form>

        <!--
          The fail-safe branch (FR-08), reachable for the first time in
          STEP-12: role attachment was declined in a controlled way. The user
          REMAINS AUTHENTICATED -- this declines a role, not the login.
        -->
        <div
          v-else-if="mobileState === 'ROLE_ONBOARDING_UNSUPPORTED'"
          data-testid="role-onboarding-unsupported"
        >
          <p class="uc-form-lead">
            {{ $t('login.mobile.roleOnboardingUnsupported.body') }}
          </p>
          <!--
            Task 12 / STEP-05 (section 8). This state had no action of any
            kind, so a user whose role attachment was declined was left on a
            dead screen -- while authenticated. Same escape as above: the role
            is declined, the session is not.
          -->
          <button
            type="button"
            class="uc-form-button"
            data-testid="role-onboarding-unsupported-continue"
            @click="continueWithCurrentAccount"
          >
            {{ $t('login.mobile.roleOnboardingUnsupported.continue') }}
          </button>
        </div>

        <!--
          Task 12 / STEP-03 (section 4). THE shared mobile-auth error region.
          Exactly one node, for every state.

          Before this, the alert lived inside the PHONE_ENTRY and OTP_ENTRY
          branches only. `submitMobileRegistration` and `submitRoleOnboarding`
          both set `mobileError` on a controlled failure (a 422 in particular),
          and it rendered nowhere at all -- the user saw a dead submit button
          and no explanation. That was the single highest-value defect in the
          flow.

          It sits after the branch container rather than inside each branch, so
          there is one node instead of four and the same message can never
          appear twice. Because every state ends with its primary CTA, that
          places the message directly beneath the action that produced it in
          every state -- adjacency without duplication.

          role="alert" carries an implicit aria-live="assertive", so the message
          is announced when it appears; the phone and OTP controls point at it
          through aria-describedby.
        -->
        <div
          v-if="mobileError"
          id="mobileAuthError"
          class="alert alert-danger"
          role="alert"
          data-testid="mobile-error"
        >
          {{ mobileError }}
        </div>

        <!--
          Task 12 / STEP-05 (section 9). This was rendered in EVERY mobile
          state. Past OTP entry it is not a secondary action any more, it is a
          way to throw away work: a user mid-registration holds a verified
          grant, and one mid-role-onboarding is already signed in -- neither
          has a password to fall back to, and both would lose what they have.
          Presentation only; the email/password path itself is untouched and
          still reachable from the two states where it makes sense.
        -->
        <p
          v-if="showEmailEscape"
          class="uc-form-secondary-action"
          data-testid="switch-to-email-action"
        >
          <button
            type="button"
            class="uc-link-button"
            data-testid="switch-to-email-button"
            @click="showEmailLogin = true"
          >
            {{ $t('login.mobile.switchToEmailButton') }}
          </button>
        </p>
      </div>

      <form
        v-if="!mobileFlowEnabled || showEmailLogin"
        data-testid="email-login-form"
      >
        <div class="uc-form-element">
          <label for="inputEmail" class="uc-form-label">{{
            $t('login.emailLabel')
          }}</label>
          <input
            id="inputEmail"
            class="uc-form-text-input"
            type="email"
            :placeholder="$t('login.emailPlaceholder')"
            v-model="credentials.email"
            required
            :autofocus="!mobileFlowEnabled"
          />
        </div>

        <div class="uc-form-element">
          <label for="inputPassword" class="uc-form-label">{{
            $t('login.passwordLabel')
          }}</label>
          <input
            id="inputPassword"
            class="uc-form-text-input"
            type="password"
            :placeholder="$t('login.passwordPlaceholder')"
            v-model="credentials.password"
            required
          />
          <router-link
            :to="`/${$i18n.locale}/resetpassword`"
            class="uc-link subtext"
          >
            {{ $t('login.forgotPassword') }}
          </router-link>
        </div>

        <button
          class="uc-form-button"
          type="submit"
          @click.prevent="signIn"
          :disabled="!isValidForm || isLoggingIn"
        >
          {{ $t('login.signInButton') }}
        </button>

        <p v-if="mobileFlowEnabled" class="uc-form-secondary-action">
          <button
            type="button"
            class="uc-link-button"
            data-testid="switch-to-mobile-button"
            @click="showEmailLogin = false"
          >
            {{ $t('login.mobile.switchToMobileButton') }}
          </button>
        </p>
      </form>

      <!--
        Hotfix / mobile-registration-entry: the legacy /sign-up link must
        never be reachable from the mobile flow -- it leads into the legacy
        password-based SignupView, not the Task-11 OTP registration state
        machine above. Registration IS the mobile flow itself, so at
        PHONE_ENTRY there is nothing to link to; past PHONE_ENTRY the user
        already holds an issued OTP or verified grant, so no exit into
        legacy signup is offered (the existing back controls in each state
        remain authoritative). Only the email-fallback screen and the
        flag-off legacy screen still offer a sign-up action.

        DESIGN-01: the PHONE_ENTRY in-flow hint is gone -- the security note
        beside the Google button is this screen's sub-CTA line now. The
        behavioural rule it encoded (no /sign-up link anywhere in the mobile
        flow) is unchanged: nothing below renders at PHONE_ENTRY.
      -->
      <i18n
        v-if="mobileFlowEnabled && showEmailLogin"
        path="login.needAccount"
        tag="p"
        class="uc-form-text"
      >
        <button
          type="button"
          class="uc-link-button"
          data-testid="switch-to-mobile-signup"
          @click="signUpAction"
        >
          {{ $t('login.signUp') }}
        </button>
      </i18n>
      <i18n
        v-else-if="!mobileFlowEnabled"
        path="login.needAccount"
        tag="p"
        class="uc-form-text"
      >
        <router-link class="uc-link" :to="`/${$i18n.locale}/sign-up`">{{
          $t('login.signUp')
        }}</router-link>
      </i18n>
    </div>
  </form-page-template>
</template>

<script>
import AnalyticsService from '@/services/AnalyticsService'
import AuthService from '@/services/AuthService'
import FormPageTemplate from '@/components/FormPageTemplate.vue'
import SimpleOtpInput from 'vue-simple-otp-input'
// DESIGN-01: the login landing is Iran-only (HUMAN DECISION 1), so the
// international vue-phone-number-input picker is gone. awesome-phonenumber (an
// existing dependency, already used by ProfileView and two dashboard modals)
// normalises a locally-typed Iranian number to a Latin-digit +98 E.164.
import PhoneNumber from 'awesome-phonenumber'
// DESIGN-01 (HUMAN DECISION 2): the Google mark for the new "continue with
// Google" entry point. Same asset the signup forms already render.
import GoogleLogo from '@/assets/google_logo.svg'
// TASK-SF-07: the redesigned student onboarding wizard. It is a STATE of this
// component (mobileState === 'STUDENT_ONBOARDING'), not a route -- src/router.js
// is NO CHANGE and there is no /onboarding/student path.
import StudentOnboardingWizard from '@/components/StudentOnboarding/StudentOnboardingWizard.vue'
import {
  saveDraft as saveOnboardingDraft,
  clearDraft as clearOnboardingDraft,
} from '@/utils/onboarding-draft'
import { EVENTS } from '@/consts'
import config from '@/config'
import i18n from '@/i18n'

// Task 12 / STEP-03 (section 9). Client-side resend throttle only. The backend
// sends no Retry-After and no OTP expiry, so this number is a UI affordance --
// it must never be described to the user as an authoritative unlock time, and
// backend rate limiting stays the real control.
const RESEND_COOLDOWN_SECONDS = 120

// P39 TR-042: what the phone field may hold -- Latin, Persian and Arabic-Indic
// digits, the usual phone punctuation (+ - ( ) .), spaces, and the invisible
// direction marks a paste from Persian text carries. Anything else is refused
// whole, never stripped: libphonenumber drops a stray letter and maps three or
// more to keypad digits, so `0912ABC4567` would otherwise become a different,
// valid number.
const PHONE_TEXT_ALLOWED = /^[0-9٠-٩۰-۹+\-().\s؜‎‏‪-‮⁦-⁩]*$/
const isAllowedPhoneText = text => PHONE_TEXT_ALLOWED.test(text || '')

export default {
  components: {
    FormPageTemplate,
    SimpleOtpInput,
    GoogleLogo,
    StudentOnboardingWizard,
  },
  created() {
    this.$store.dispatch('app/hideNavigation')
    localStorage.removeItem('isSSOSignUpRedirect')
  },
  computed: {
    isValidForm() {
      const { email, password } = this.credentials
      return email && password
    },
    // CR-07, CR-03: discoverability only -- a build-time value, never a
    // security boundary. The backend gate is authoritative; when it is off
    // the /auth/mobile/* calls this view makes fail safely regardless of
    // this flag (mapMobileError's 404 branch).
    mobileFlowEnabled() {
      return config.mobilePhoneAuthEnabled
    },
    // SX-02 (doc 22 §4-§5): the ONE central Student teacher/volunteer
    // capability. OFF = registering AS a teacher is Coming Soon: the role
    // card stays (accepted DESIGN-03 layout) but is badged and opens the
    // shared dialog instead of the volunteer registration form. Existing
    // volunteer accounts are untouched -- this screen only ever runs for a
    // phone with NO account yet.
    teacherExperienceAvailable() {
      return (
        this.$store.getters[
          'featureFlags/isStudentTeacherExperienceAvailable'
        ] === true
      )
    },
    isValidPhone() {
      return Boolean(this.phoneInputInfo && this.phoneInputInfo.isValid)
    },
    isValidOtp() {
      return /^\d{6}$/.test(this.otpCode)
    },
    // Read-only orientation only. The submitted body never carries a phone --
    // the backend re-reads the verified number from the registration grant.
    phoneDisplay() {
      return this.phoneInputInfo?.formatNational || this.phone
    },
    canSubmitRegistration() {
      const { firstName, lastName, terms } = this.registration
      return (
        Boolean(firstName.trim()) &&
        Boolean(lastName.trim()) &&
        terms === true &&
        !this.isRegistering
      )
    },
    // Task 12 / STEP-03. Attributes handed to each OTP box. `one-time-code`
    // plus a numeric inputmode is what makes the OS keyboard and the iOS SMS
    // autofill suggestion behave on a phone.
    otpInputProps() {
      return {
        inputmode: 'numeric',
        autocomplete: 'one-time-code',
        'aria-invalid': this.mobileError ? 'true' : null,
      }
    },
    // Guards the resend control: never while a request is in flight, never
    // during the cooldown.
    canResendCode() {
      return (
        !this.isSubmitting && !this.isResending && this.resendCooldown === 0
      )
    },
    // Task 12 / STEP-02 (A). The card's single <h1>, derived from the state the
    // user is actually in rather than a constant.
    //
    // Reads only existing state -- it decides nothing and changes no transition.
    // Most branches reuse the heading string each state already owned as its own
    // <h2>; those <h2>s are removed, so this reparents them rather than
    // duplicating them. Only the three states that never had a heading of their
    // own (phone entry, OTP entry, pending) needed new keys.
    pageHeading() {
      if (!this.mobileFlowEnabled || this.showEmailLogin) {
        return this.$t('login.header')
      }

      switch (this.mobileState) {
        case 'OTP_ENTRY':
          return this.$t('login.mobile.heading.otpEntry')
        case 'RESOLVING':
        case 'SESSION_ESTABLISHING':
          return this.$t('login.mobile.heading.pending')
        // AUTH-G1 (02_* §15.11): three new states. PHONE_ENTRY/OTP_ENTRY
        // themselves are reused unchanged for the link flow except for this
        // one heading override -- see isGoogleLinkFlow below.
        case 'GOOGLE_CREATE_ACCOUNT_LOADING':
          return this.$t('login.mobile.heading.pending')
        case 'GOOGLE_CREATE_ACCOUNT':
          return this.$t('login.mobile.google.createAccount.header')
        case 'GOOGLE_LINK_UNAVAILABLE':
          return this.$t('login.mobile.google.linkUnavailable.header')
        case 'PHONE_ENTRY':
          return this.isGoogleLinkFlow
            ? this.$t('login.mobile.google.linkRequired.header')
            : this.$t('login.mobile.heading.phoneEntry')
        case 'REGISTRATION_REQUIRED':
          return this.$t('login.mobile.registrationRequired.header')
        case 'REGISTRATION_FORM':
          return this.registrationRole === 'volunteer'
            ? this.$t('login.mobile.registrationForm.volunteerHeader')
            : this.$t('login.mobile.registrationForm.studentHeader')
        case 'ACCOUNT_CONFLICT':
          return this.$t('login.mobile.accountConflict.header')
        // Task 12 / STEP-05 (section 7): name the role being added rather than
        // saying "this role" about a role the screen never identifies.
        case 'ROLE_ONBOARDING_REQUIRED':
          return this.roleIntent === 'student'
            ? this.$t('login.mobile.roleOnboardingRequired.headerStudent')
            : this.$t('login.mobile.roleOnboardingRequired.headerVolunteer')
        case 'ROLE_ONBOARDING_UNSUPPORTED':
          return this.$t('login.mobile.roleOnboardingUnsupported.header')
        default:
          return this.$t('login.mobile.heading.phoneEntry')
      }
    },

    // DESIGN-01 is the login-landing composition: the mobile flow is on, the
    // email fallback is not showing, and the user is at phone entry. DESIGN-03
    // and the email-fallback screen must be visually untouched by this task, so
    // every DESIGN-01-only element -- and the FormPageTemplate variant -- is
    // gated on this.
    isDesign01() {
      return (
        this.mobileFlowEnabled &&
        !this.showEmailLogin &&
        this.mobileState === 'PHONE_ENTRY'
      )
    },

    // DESIGN-02 is the OTP composition: mobile flow on, email fallback not
    // showing, user at OTP_ENTRY. Gates the in-card lockup / eyebrow / top
    // change-number link and the FormPageTemplate `design-02` shell.
    isDesign02() {
      return (
        this.mobileFlowEnabled &&
        !this.showEmailLogin &&
        this.mobileState === 'OTP_ENTRY'
      )
    },

    // WP-04V-B round 2. DESIGN-03 is the role-selection composition: mobile
    // flow on, email fallback not showing, user at REGISTRATION_REQUIRED.
    // Gates the in-card lockup / back link / eyebrow and the canonical role
    // cards. This is the screen the human's round-1 UAT rejected.
    isDesign03() {
      return (
        this.mobileFlowEnabled &&
        !this.showEmailLogin &&
        this.mobileState === 'REGISTRATION_REQUIRED'
      )
    },

    // TASK-SF-07: the 3-step student onboarding wizard state. Drives the
    // dedicated `student-onboarding` FormPageTemplate variant and the
    // onboarding card geometry. Reachable only via the student role card /
    // a preselected student roleIntent -- never the volunteer branch.
    isStudentOnboardingState() {
      return this.mobileState === 'STUDENT_ONBOARDING'
    },

    // The FormPageTemplate variant for the current state. PHONE_ENTRY,
    // OTP_ENTRY and STUDENT_ONBOARDING each add their own composition on top
    // of the shared shell; WP-04V-B round 2 puts every OTHER state of the
    // mobile auth journey on that same shell via the plain `auth` variant.
    //
    // Human visual UAT round 1 failed here: role selection, registration,
    // account conflict, the resolve/pending states and role onboarding all
    // returned null, so mid-journey the user dropped out of the Borna shell
    // and back onto the legacy UpChieve composition.
    //
    // The email fallback and the flag-off legacy path still return null: they
    // are the legacy login surface, not the redesigned journey.
    formPageVariant() {
      if (this.isDesign01) return 'design-01'
      if (this.isDesign02) return 'design-02'
      if (this.isStudentOnboardingState) return 'student-onboarding'
      if (this.mobileFlowEnabled && !this.showEmailLogin) return 'auth'
      return null
    },

    // True whenever the card is sitting on the Borna auth shell, so the shared
    // card geometry applies once instead of being restated per screen.
    isBornaAuthCard() {
      return this.formPageVariant !== null
    },

    // Task 12 / STEP-05 (section 9), amended by DESIGN-01 and now DESIGN-02:
    // the email/password escape is NOT offered on the redesigned mobile-auth
    // journey (PHONE_ENTRY or OTP_ENTRY) -- it follows DESIGN-01. This is
    // presentation-only: `showEmailLogin`, the email/password form, its return
    // control, `signIn()` and the flag-off legacy path are all untouched, and
    // the form still renders whenever `showEmailLogin` is set.
    showEmailEscape() {
      return false
    },

    // DESIGN-02: the destination number is shown back to the user MASKED --
    // first four and last four digits kept, the middle three replaced by
    // bullets, digits localised for `fa`. Display only: `phone`,
    // `phoneInputInfo` and every value sent to the API are untouched; this is
    // derived from the already-formatted national string.
    maskedPhone() {
      const digits = String(this.phoneDisplay || '').replace(/\D/g, '')
      if (digits.length < 8) return this.localizeDigits(digits)
      const masked = `${digits.slice(0, 4)}•••${digits.slice(-4)}`
      return this.localizeDigits(masked)
    },

    // DESIGN-02: the remaining LOCAL resend throttle, as a compact mm:ss
    // suffix on the resend button. It is NOT an OTP expiry and NOT an
    // authoritative server unlock time -- see RESEND_COOLDOWN_SECONDS and
    // `resendMobileCode`, which re-issues the SAME challenge on the SAME
    // number.
    resendCountdownLabel() {
      const total = Math.max(0, this.resendCooldown)
      const mm = String(Math.floor(total / 60)).padStart(2, '0')
      const ss = String(total % 60).padStart(2, '0')
      return this.localizeDigits(`${mm}:${ss}`)
    },

    // Task 12 / STEP-05 (section 7). Names the role actually being added, so
    // the screen stops saying "this role" about a role it never identifies.
    roleOnboardingSubmitLabel() {
      return this.roleIntent === 'student'
        ? this.$t('login.mobile.roleOnboardingRequired.submitStudent')
        : this.$t('login.mobile.roleOnboardingRequired.submitVolunteer')
    },

    // Task 12 / STEP-05 (section 10).
    registrationLead() {
      return this.registrationRole === 'volunteer'
        ? this.$t('login.mobile.registrationForm.volunteerLead')
        : this.$t('login.mobile.registrationForm.studentLead')
    },
  },
  data() {
    let error
    if (this.$route.query['401'] === 'true') {
      error = this.$root.$t('login.error.401')
    }
    if (this.$route.query['400'] === 'true') {
      AnalyticsService.captureEvent(
        EVENTS.USER_DOES_NOT_HAVE_LINKED_GOOGLE_ACCOUNT
      )
      error = this.$root.$t('login.error.400')
    }
    // AUTH-G1 (02_* §15.9, §15.11): the unified resolver never emits
    // ?400=true/?401=true -- it redirects to /{locale}/login?google=
    // register|link|link-unavailable on a routing outcome, or
    // ?googleError=<STABLE_CODE> on failure (never a raw provider message,
    // 00_* AG-08). Read once here, exactly like the 400/401 block above.
    let initialMobileState = 'PHONE_ENTRY'
    const googleFlow = this.$route.query.google
    if (googleFlow === 'register') {
      initialMobileState = 'GOOGLE_CREATE_ACCOUNT_LOADING'
    } else if (googleFlow === 'link-unavailable') {
      initialMobileState = 'GOOGLE_LINK_UNAVAILABLE'
    }
    const googleErrorCode = this.$route.query.googleError
    if (typeof googleErrorCode === 'string') {
      error = this.mapGoogleErrorCode(googleErrorCode)
    }
    this.isLoggingIn = false
    return {
      credentials: {
        email: '',
        password: '',
      },
      error,
      isLoggingIn: false,

      // Task 11 / STEP-08 mobile OTP login state -- in-memory only.
      showEmailLogin: false,
      mobileState: initialMobileState,
      // AUTH-G1: `google=link` reuses the EXISTING phone/OTP states
      // (PHONE_ENTRY/OTP_ENTRY/RESOLVING) rather than duplicating them --
      // this flag only changes what happens after a successful OTP
      // resolution (link vs. ordinary login) and the copy shown at
      // PHONE_ENTRY. Never survives a full reset (see resetMobileFlow).
      isGoogleLinkFlow: googleFlow === 'link',
      // The safe subset the backend exposes for the pending Google identity
      // (email, given name) -- never a sub, issuer or token. Populated by
      // fetchGooglePending(); absent until then.
      googlePendingEmail: null,
      googlePendingFirstName: null,
      isConfirmingGoogleAccount: false,
      googleAccountFirstName: '',
      googleAccountLastName: '',
      googleAccountTerms: false,
      phone: '',
      // P39 TR-042: the field's last value made only of allowed characters --
      // what a refused insertion falls back to.
      lastAllowedPhone: '',
      phoneInputInfo: {},
      otpCode: '',
      challengeId: null,
      ownershipGrantId: null,
      registrationGrantId: null,
      mobileError: null,
      // Task 11 / STEP-12 (Q-011): the role the person came here to use. Read
      // from the route (?role=student|volunteer) so a role-specific entry
      // point carries its intent through login. Null means "no intent", which
      // is honoured literally -- a dual-role user with no intent is ASKED
      // rather than sent to whichever dashboard a boolean happened to pick.
      roleIntent:
        this.$route.query.role === 'student' ||
        this.$route.query.role === 'volunteer'
          ? this.$route.query.role
          : null,
      isOnboardingRole: false,
      roleOnboardingTerms: false,
      // Task 11 new-user registration. `registrationRole` is the INITIAL role
      // whose profile will be created for a brand-new account -- distinct from
      // `roleIntent` (which role an EXISTING account was entered for) and from
      // STEP-12's activeRole (which role an existing identity is acting as).
      registrationRole: null,
      isRegistering: false,
      // TASK-SF-07: server EMAIL_CONFLICT message surfaced on wizard step 3
      // without unwinding the wizard; `pendingOnboardingValues` carries the
      // typed values across an OTP re-verification when the grant expires
      // mid-wizard, so they can be re-keyed to the fresh grant.
      onboardingEmailError: null,
      pendingOnboardingValues: null,
      registration: {
        firstName: '',
        lastName: '',
        email: '',
        terms: false,
      },
      // Single-flight guard: set synchronously before the first await in
      // every mobile transition method and checked at each method's entry,
      // so at most one send/confirm/resolve/login round trip is ever in
      // flight -- a stale response cannot arrive after a newer transition
      // has already started, because a newer one cannot start until this is
      // cleared (RR-05, section 16).
      isSubmitting: false,

      // Task 12 / STEP-03. Resend state. `resendCooldown` counts seconds down
      // to zero and gates the control; `resendNotice` is the visible "we sent
      // another one" confirmation. Its in-flight guard is deliberately separate
      // from `isSubmitting` so a resend is never mistaken for a state
      // transition. The interval id itself is kept off `data` -- it is not
      // render state and must not be made reactive.
      isResending: false,
      resendCooldown: 0,
      resendNotice: null,
    }
  },
  watch: {
    mobileState(newState) {
      if (newState === 'OTP_ENTRY') {
        this.focusOtpInput()
      }
    },
    phone(value) {
      if (isAllowedPhoneText(value)) this.lastAllowedPhone = value
    },
  },
  // Task 12 / STEP-03 (section 10). Give the first field focus so a returning
  // user can start typing their number immediately.
  mounted() {
    if (this.mobileFlowEnabled && !this.showEmailLogin) this.focusPhoneInput()
    // AUTH-G1 (02_* §15.4, §15.9): the pending Google identity lives
    // server-side in the session -- it is fetched once here for both
    // states that can display it. GOOGLE_CREATE_ACCOUNT_LOADING blocks on
    // it (there is nothing to show without it); GOOGLE_LINK_UNAVAILABLE and
    // the isGoogleLinkFlow PHONE_ENTRY case fetch it non-blockingly, purely
    // to enrich copy with the matched email -- their forward path never
    // depends on the fetch succeeding.
    if (this.mobileState === 'GOOGLE_CREATE_ACCOUNT_LOADING') {
      this.fetchGooglePendingForRegistration()
    } else if (
      this.mobileState === 'GOOGLE_LINK_UNAVAILABLE' ||
      this.isGoogleLinkFlow
    ) {
      this.fetchGooglePending()
    }
  },
  // The cooldown interval outlives a route change unless it is cleared here.
  beforeDestroy() {
    this.stopResendCooldown()
  },
  methods: {
    signIn() {
      if (!this.isValidForm) return
      this.isLoggingIn = true
      AuthService.login({
        email: this.credentials.email,
        password: this.credentials.password,
      })
        .then(data => {
          this.$store.commit('user/setUser', data.user)
          this.$router.push(
            this.$route.query.redirect || `/${i18n.locale}/dashboard`
          )
        })
        .catch(() => {
          this.error = this.$root.$t('login.error.authServiceError')
        })
        .finally(() => {
          this.isLoggingIn = false
        })
    },

    // DESIGN-01 (HUMAN DECISION 1). Derive the SAME `phoneInputInfo` shape the
    // rest of this flow already consumes (isValid / e164 / formatNational) from
    // a plain text field. awesome-phonenumber accepts 0912…, 912…, spacing, and
    // Persian *or* Latin digits when given an 'IR' region hint;
    // `getNumber('e164')` always returns a Latin-digit +98… string -- so
    // nothing downstream changes, and a bare 0912… or a Persian-digit string
    // can never reach `sendMobileChallenge`. `isMobile()` keeps a valid
    // Iranian landline from passing as an account identity.
    //
    // P39 TR-042: the last line of defence. A value that still carries a
    // refused character (an IME commit, autofill, or a browser without
    // `beforeinput`) is put back to the last allowed value, whole; one in the
    // middle of a composition is never valid.
    onPhoneInput(event) {
      const field = event && event.target
      const composing = Boolean(
        (event && event.isComposing) || (field && field.composing)
      )
      if (!composing && !isAllowedPhoneText(this.phone)) {
        this.restoreAllowedPhone(field)
        return
      }
      const parsed = new PhoneNumber(this.phone || '', 'IR')
      // Iran-only: a number typed with an explicit foreign country code (e.g.
      // +1…) parses as that country and must not pass -- the region check
      // rejects it even when libphonenumber would call it a valid mobile.
      const isValid =
        isAllowedPhoneText(this.phone) &&
        parsed.isValid() &&
        parsed.isMobile() &&
        parsed.getRegionCode() === 'IR'
      this.phoneInputInfo = {
        isValid,
        e164: isValid ? parsed.getNumber('e164') : '',
        formatNational: parsed.getNumber('national') || this.phone,
      }
    },
    // P39 TR-042: refuse a typed, dropped or replacement text that carries a
    // character the field does not take, before it lands. Deletions carry no
    // text and pass; a composition is checked once it is committed.
    onPhoneBeforeInput(event) {
      if (event.isComposing || event.inputType === 'insertCompositionText') {
        return
      }
      let text = typeof event.data === 'string' ? event.data : ''
      if (!text && event.dataTransfer) {
        text = event.dataTransfer.getData('text/plain') || ''
      }
      if (!isAllowedPhoneText(text)) event.preventDefault()
    },
    // P39 TR-042: a paste is taken whole or not at all.
    onPhonePaste(event) {
      const data = event.clipboardData
      const text = data
        ? data.getData('text/plain') || data.getData('text')
        : ''
      if (!isAllowedPhoneText(text)) event.preventDefault()
    },
    restoreAllowedPhone(field) {
      const restored = this.lastAllowedPhone
      // Keep the caret where the refused text would have started.
      let caret = null
      if (field && typeof field.selectionStart === 'number') {
        const grown = (field.value || '').length - restored.length
        caret = Math.max(0, field.selectionStart - Math.max(0, grown))
      }
      this.phone = restored
      if (field) {
        field.value = restored
        if (caret !== null && typeof field.setSelectionRange === 'function') {
          const at = Math.min(caret, restored.length)
          field.setSelectionRange(at, at)
        }
      }
    },

    // DESIGN-01 (HUMAN DECISION 2). A new entry point to the EXISTING Google
    // AUTH-G1 (00_* §16, 02_* §15.9, §15.11): "Continue with Google" now
    // targets the unified resolver, carrying the current locale so the
    // eventual redirect (success, create-account, link-required, or error)
    // comes back locale-qualified (00_* AG-08) -- no blank page, regardless
    // of outcome. The backend decides login vs. create-account vs. secure
    // linking; nothing about that decision is made here. Same navigation
    // shape as before (a full top-level redirect), just a different target.
    signInWithGoogle() {
      AnalyticsService.captureEvent(EVENTS.USER_CLICKED_SIGN_IN_WITH_GOOGLE)
      window.location.replace(
        `${config.serverRoot}/auth/google?locale=${i18n.locale}`
      )
    },

    // --- Task 12 / STEP-03: focus management -------------------------------
    //
    // Defensive: a missing ref must never throw inside a state transition.

    focusPhoneInput() {
      this.$nextTick(() => {
        const input = this.$refs.mobilePhoneInput
        if (input && typeof input.focus === 'function') input.focus()
      })
    },

    // SimpleOtpInput renders one <input> per digit; focus belongs on the first
    // empty box, which after a clear is simply the first one.
    focusOtpInput() {
      this.$nextTick(() => {
        const root = this.$refs.mobileOtpInput && this.$refs.mobileOtpInput.$el
        const input = root && root.querySelector('input')
        if (input && typeof input.focus === 'function') input.focus()
      })
    },

    // DESIGN-02: presentation-only digit localisation for the masked number
    // and the resend countdown. `fa` renders Persian digits; every other
    // locale is left as ASCII. Never applied to a value that is submitted.
    localizeDigits(value) {
      if (this.$i18n.locale !== 'fa') return String(value)
      const persian = ['۰', '۱', '۲', '۳', '۴', '۵', '۶', '۷', '۸', '۹']
      return String(value).replace(/[0-9]/g, d => persian[Number(d)])
    },

    // --- Task 12 / STEP-03: resend + client-side cooldown ------------------

    startResendCooldown(seconds = RESEND_COOLDOWN_SECONDS) {
      this.stopResendCooldown()
      this.resendCooldown = seconds
      this.resendTimerId = setInterval(() => {
        this.resendCooldown -= 1
        if (this.resendCooldown <= 0) this.stopResendCooldown()
      }, 1000)
    },

    stopResendCooldown() {
      if (this.resendTimerId) {
        clearInterval(this.resendTimerId)
        this.resendTimerId = null
      }
      this.resendCooldown = 0
    },

    // Re-issues the SAME challenge request against the SAME number. No new
    // endpoint, no new contract: this is `sendMobileChallenge` again, which is
    // exactly what the user pressing "resend" means. The old challengeId is
    // replaced by the new one, so the previously typed code is cleared rather
    // than left to be confirmed against a challenge that no longer applies.
    async resendMobileCode() {
      if (!this.canResendCode || !this.isValidPhone) return
      this.isResending = true
      this.mobileError = null
      this.resendNotice = null
      try {
        const { challengeId } = await AuthService.sendMobileChallenge(
          this.phoneInputInfo.e164
        )
        this.challengeId = challengeId
        this.otpCode = ''
        this.resendNotice = this.$t('login.mobile.resendSent')
        this.startResendCooldown()
        this.focusOtpInput()
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'send')
      } finally {
        this.isResending = false
      }
    },

    // DESIGN-01 (HUMAN DECISION 4): the pre-OTP role-intent chips are removed
    // from PHONE_ENTRY -- role selection now happens after OTP on DESIGN-03. The
    // `roleIntent` field and every downstream consumer of it are UNCHANGED: it
    // still seeds from `?role=student|volunteer` (a role-specific entry point),
    // still gates ROLE_ONBOARDING_REQUIRED, and still drives the post-login
    // `setActiveRole`. Only the on-screen producer chip UI is gone.

    // Task 12 / STEP-05 (section 8). Declines the ROLE, never the session.
    //
    // The account is already authenticated by the time either onboarding state
    // renders, so there is nothing to log out of and nothing to re-verify. No
    // role is added or removed, no store action is dispatched and no request is
    // made -- the pending intent is simply dropped, which is what makes the
    // dashboard treat this as an ordinary arrival. A dual-role account with no
    // acting role is then asked by DashboardView under STEP-04's rules rather
    // than being resolved here.
    continueWithCurrentAccount() {
      this.roleIntent = null
      this.mobileError = null
      this.$router.push(`/${i18n.locale}/dashboard`)
    },

    // Full reset: an explicit user "start over" action, so the error is
    // cleared along with the rest of the flow's state.
    resetMobileFlow() {
      this.returnToPhoneEntry()
      this.mobileError = null
    },

    // Hotfix / mobile-registration-entry: the email-fallback screen's
    // sign-up action returns to the mobile flow instead of navigating to
    // legacy /sign-up -- registration for a new user IS the mobile flow.
    signUpAction() {
      this.showEmailLogin = false
      this.resetMobileFlow()
    },

    // Internal reset used after a failure: returns to phone entry without
    // clobbering the mobileError the caller just set.
    returnToPhoneEntry() {
      this.mobileState = 'PHONE_ENTRY'
      this.otpCode = ''
      this.challengeId = null
      this.ownershipGrantId = null
      this.registrationGrantId = null
      // Task 11 new-user registration: the half-filled form and the chosen
      // initial role belong to the grant that is being discarded here, so they
      // must not survive into a fresh attempt with a different number.
      this.registrationRole = null
      this.registration = {
        firstName: '',
        lastName: '',
        email: '',
        terms: false,
      }
      // Task 12 / STEP-03: the cooldown belonged to the challenge just
      // discarded. Leaving it running would block the resend control on a
      // fresh attempt with a different number.
      this.stopResendCooldown()
      this.resendNotice = null
    },

    async sendMobileCode() {
      if (this.isSubmitting || !this.isValidPhone) return
      this.isSubmitting = true
      this.mobileError = null
      try {
        const { challengeId } = await AuthService.sendMobileChallenge(
          this.phoneInputInfo.e164
        )
        this.challengeId = challengeId
        this.otpCode = ''
        this.resendNotice = null
        // Task 12 / STEP-03: a code has just gone out, so the resend throttle
        // starts here rather than only after the first manual resend.
        this.startResendCooldown()
        this.mobileState = 'OTP_ENTRY'
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'send')
      } finally {
        this.isSubmitting = false
      }
    },

    async confirmMobileCode() {
      if (this.isSubmitting || !this.isValidOtp) return
      this.isSubmitting = true
      this.mobileError = null
      try {
        const {
          ownershipGrantId,
        } = await AuthService.confirmMobileVerification({
          challengeId: this.challengeId,
          code: this.otpCode,
        })
        this.ownershipGrantId = ownershipGrantId
        this.otpCode = ''
        await this.resolveMobileIdentity()
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'confirm')
        // A wrong/expired code does not invalidate the still-issued
        // challenge (Task 04 precedent) -- stay on OTP_ENTRY so it can be
        // retried. Only a "feature unavailable" response returns to phone
        // entry, since there is nothing left to retry against.
        if (err && err.status === 404) {
          this.returnToPhoneEntry()
        } else {
          // Task 12 / STEP-03 (section 10). Retry has to be immediate: empty
          // the boxes and put the caret back in the first one, so the user is
          // never left hunting for focus or deleting six digits by hand.
          this.otpCode = ''
          this.resendNotice = null
          this.focusOtpInput()
        }
      } finally {
        this.isSubmitting = false
      }
    },

    async resolveMobileIdentity() {
      this.mobileState = 'RESOLVING'
      try {
        const result = await AuthService.resolveMobileIdentity(
          this.ownershipGrantId
        )
        if (result.outcome === 'EXISTING_ACCOUNT') {
          // AUTH-G1 (00_* AG-05, 02_* §15.5): the phone this call just
          // resolved to a single existing account IS the ownership proof --
          // link the pending Google identity to THAT account rather than
          // signing in bare. `isGoogleLinkFlow` never changes what resolve
          // itself does; it only changes what happens after a match.
          if (this.isGoogleLinkFlow) {
            await this.linkGoogleAccount()
          } else {
            await this.establishMobileSession()
          }
        } else if (result.outcome === 'REGISTRATION_REQUIRED') {
          // AUTH-G1: the verified phone matched NO existing account. In the
          // link flow this is a failed ownership-proof attempt, never an
          // invitation to register a new (possibly duplicate) account --
          // 00_* AG-02 forbids creating a second account merely to escape a
          // conflict. Offer retry with a different number; the grant is
          // discarded exactly as any other retry does.
          if (this.isGoogleLinkFlow) {
            this.mobileError = this.$t(
              'login.mobile.google.linkRequired.noMatch'
            )
            this.returnToPhoneEntry()
            return
          }
          // STEP-09/10/11 boundary: retained only for a future step: no
          // registration call is made with it in this slice.
          this.registrationGrantId = result.registrationGrantId

          // TASK-SF-07: the grant expired mid-wizard and the user just
          // re-verified. Re-key the carried-over draft to the fresh grant
          // and drop them straight back into the wizard -- no typed value is
          // lost (02_* §3.8).
          if (this.pendingOnboardingValues) {
            saveOnboardingDraft(
              this.registrationGrantId,
              this.pendingOnboardingValues
            )
            this.pendingOnboardingValues = null
            this.registrationRole = 'student'
            this.mobileState = 'STUDENT_ONBOARDING'
            return
          }

          // Task 12 / STEP-05 (section 6). A new user who already said why
          // they were here is not asked the identical question a second time;
          // their intent seeds `registrationRole` and the flow opens directly.
          //
          // `registrationRole` stays the sole authority over which account is
          // created -- intent only supplies its initial value, and the "choose
          // a different role" action still returns to the full choice, so
          // nothing is locked in. With no intent, REGISTRATION_REQUIRED is
          // reached exactly as before. TASK-SF-07: a student intent enters the
          // redesigned wizard; a volunteer intent is unchanged.
          if (this.roleIntent === 'student') {
            this.registrationRole = 'student'
            this.mobileState = 'STUDENT_ONBOARDING'
          } else if (
            this.roleIntent === 'volunteer' &&
            this.teacherExperienceAvailable
          ) {
            this.registrationRole = 'volunteer'
            this.mobileState = 'REGISTRATION_FORM'
          } else {
            // P40 TR-004: while teacher acquisition is Coming Soon, a
            // `?role=volunteer` intent gets exactly what the badged card gives
            // (the shared dialog, via the same gate) and the role choice --
            // never the volunteer registration form.
            if (this.roleIntent === 'volunteer') {
              this.chooseRegistrationRole('volunteer')
            }
            this.mobileState = 'REGISTRATION_REQUIRED'
          }
        } else if (result.outcome === 'ACCOUNT_CONFLICT') {
          this.mobileState = 'ACCOUNT_CONFLICT'
        } else {
          this.mobileError = this.$t('login.mobile.error.generic')
          this.returnToPhoneEntry()
        }
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'resolve')
        this.returnToPhoneEntry()
      }
    },

    async establishMobileSession() {
      this.mobileState = 'SESSION_ESTABLISHING'
      try {
        const result = await AuthService.establishMobileSession(
          this.ownershipGrantId
        )
        if (result && result.authenticated) {
          // Task 11 / STEP-12 (Q-011): the session now exists and is NOT
          // revoked by anything below. If the person came for a role this
          // account does not hold, offer to attach it to THIS account -- never
          // a redirect to /sign-up, which would create a second identity for
          // the same human, and never a second OTP, because the session they
          // just established is already the ownership proof.
          await this.$store.dispatch('user/fetchUser')
          const roles = this.$store.getters['user/roles']

          // P40 TR-004: adding the volunteer role to an existing account is
          // the same Coming Soon acquisition the profile's upgrade card
          // gates, so `?role=volunteer` cannot reach it either: show the
          // shared dialog and continue as an ordinary login.
          if (
            this.roleIntent === 'volunteer' &&
            !roles.includes('volunteer') &&
            !this.teacherExperienceAvailable
          ) {
            this.$store.dispatch('app/comingSoon/show', {
              variant: 'teacher',
              reason: 'register',
            })
            this.roleIntent = null
          }

          if (this.roleIntent && !roles.includes(this.roleIntent)) {
            this.mobileState = 'ROLE_ONBOARDING_REQUIRED'
            return
          }
          if (this.roleIntent) {
            await this.$store.dispatch('user/setActiveRole', this.roleIntent)
          }

          // Q-013 owner decision for this slice: existing-account mobile
          // login always goes to the locale-aware dashboard. Deliberately
          // does not consult $route.query.redirect -- that behaviour is
          // password-login's, and Task 11 must not widen it (SR-14). A
          // dual-role user arriving with no intent is asked by the dashboard
          // itself (roleSelectionRequired), not guessed at here.
          this.$router.push(`/${i18n.locale}/dashboard`)
        } else {
          this.mobileError = this.$t('login.mobile.error.generic')
          this.returnToPhoneEntry()
        }
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'login')
        this.returnToPhoneEntry()
      }
    },

    // AUTH-G1 (00_* AG-05, 02_* §15.5): the OTP-proven Google-linking
    // sibling of establishMobileSession above -- same grant-id-only
    // authority, same SESSION_ESTABLISHING presentation. The pending
    // Google identity itself is read server-side from the session; this
    // call carries only the ownershipGrantId, exactly like every other
    // /auth/mobile/* call in this file.
    async linkGoogleAccount() {
      this.mobileState = 'SESSION_ESTABLISHING'
      try {
        const result = await AuthService.linkGoogleAccount(
          this.ownershipGrantId
        )
        if (result && result.authenticated) {
          await this.$store.dispatch('user/fetchUser')
          this.$router.push(`/${i18n.locale}/dashboard`)
        } else {
          this.mobileError = this.$t('login.mobile.error.generic')
          this.returnToPhoneEntry()
        }
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'link')
        this.returnToPhoneEntry()
      }
    },

    // AUTH-G1 (02_* §15.9): non-blocking enrichment fetch -- used by both
    // GOOGLE_LINK_UNAVAILABLE and the isGoogleLinkFlow PHONE_ENTRY case.
    // Neither state's forward path depends on this succeeding, so a
    // failure here is silently ignored: the copy simply omits the matched
    // email rather than blocking or erroring the screen.
    async fetchGooglePending() {
      try {
        const pending = await AuthService.getGooglePending()
        this.googlePendingEmail = pending.email || null
        this.googlePendingFirstName = pending.firstName || null
      } catch (err) {
        // Non-blocking by design -- see the comment above.
      }
    },

    // AUTH-G1 (02_* §15.4): GOOGLE_CREATE_ACCOUNT_LOADING blocks on this --
    // there is nothing to show without it. A missing/expired pending
    // identity (direct navigation, a refresh past the 900s TTL) is not an
    // error state of its own; it simply means "start over."
    async fetchGooglePendingForRegistration() {
      try {
        const pending = await AuthService.getGooglePending()
        this.googlePendingEmail = pending.email || null
        this.googlePendingFirstName = pending.firstName || null
        this.mobileState = 'GOOGLE_CREATE_ACCOUNT'
      } catch (err) {
        this.mobileError = this.$t('login.mobile.google.error.LINK_EXPIRED')
        this.returnToPhoneEntry()
      }
    },

    // AUTH-G1 (00_* AG-04, 02_* §15.4): consent + atomic creation. `terms`
    // is sent unconditionally true -- the submit button is disabled until
    // the checkbox is checked, mirroring ROLE_ONBOARDING_REQUIRED's own
    // submit-gating pattern rather than duplicating a second consent
    // representation.
    async confirmGoogleAccountCreation() {
      if (!this.googleAccountTerms || this.isConfirmingGoogleAccount) return
      this.isConfirmingGoogleAccount = true
      this.mobileError = null
      try {
        const result = await AuthService.confirmGoogleRegistration({
          terms: true,
          firstName: this.googleAccountFirstName,
          lastName: this.googleAccountLastName,
        })
        if (result && result.authenticated) {
          await this.$store.dispatch('user/fetchUser')
          await this.$store.dispatch('user/setActiveRole', 'student')
          this.$router.push(`/${i18n.locale}/dashboard`)
        } else {
          this.mobileError = this.$t('login.mobile.error.generic')
        }
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'googleRegister')
      } finally {
        this.isConfirmingGoogleAccount = false
      }
    },

    // AUTH-G1 (00_* AG-08): maps the small, stable set of query-string error
    // codes the unified resolver's redirects carry -- never the raw
    // provider message underneath them (that never reaches the frontend at
    // all). An unrecognised code (a future addition, a stale bookmark)
    // falls back to the generic message rather than showing nothing.
    mapGoogleErrorCode(code) {
      const known = [
        'GOOGLE_OAUTH_CANCELLED',
        'GOOGLE_OAUTH_FAILED',
        'GOOGLE_ACCOUNT_CONFLICT',
        'GOOGLE_LINK_EXPIRED',
        'GOOGLE_LINK_INVALID',
        'GOOGLE_ACCOUNT_DISABLED',
        'GOOGLE_REGISTRATION_FAILED',
      ]
      const key = known.includes(code) ? code : 'GENERIC'
      return this.$t(`login.mobile.google.error.${key}`)
    },

    // Task 11 new-user registration: pick which account to CREATE. This runs
    // before any account exists, so it is not role onboarding and not an
    // acting-role switch -- nothing is attached to an existing identity here.
    chooseRegistrationRole(role) {
      // SX-02: NEW teacher/volunteer acquisition is Coming Soon while the
      // capability is OFF -- nothing about the registration state changes.
      if (role === 'volunteer' && !this.teacherExperienceAvailable) {
        this.$store.dispatch('app/comingSoon/show', {
          variant: 'teacher',
          reason: 'register',
        })
        return
      }
      this.registrationRole = role
      this.mobileError = null
      // TASK-SF-07: the student card enters the redesigned 3-step wizard
      // (DESIGN-04/05/09); the volunteer card is unchanged.
      this.mobileState =
        role === 'student' ? 'STUDENT_ONBOARDING' : 'REGISTRATION_FORM'
    },

    // Task 11 / STEP-10 (student) and STEP-11 (volunteer). The verified
    // registrationGrantId held since resolution is the only identity
    // authority: no second OTP, no new challenge, and no phone/password/
    // userId in the body (AuthService rebuilds it from an allowlist).
    async submitMobileRegistration() {
      if (this.isRegistering || !this.canSubmitRegistration) return
      this.isRegistering = true
      this.mobileError = null
      try {
        const payload = {
          registrationGrantId: this.registrationGrantId,
          firstName: this.registration.firstName.trim(),
          lastName: this.registration.lastName.trim(),
          terms: this.registration.terms,
          email: this.registration.email.trim(),
        }

        const result =
          this.registrationRole === 'volunteer'
            ? await AuthService.registerMobileVolunteer(payload)
            : await AuthService.registerMobileStudent(payload)

        // The backend establishes the session itself on success, so there is
        // nothing to log in to here -- only state to load.
        if (result && result.authenticated) {
          await this.$store.dispatch('user/fetchUser')
          await this.$store.dispatch(
            'user/setActiveRole',
            this.registrationRole
          )
          this.$router.push(`/${i18n.locale}/dashboard`)
          return
        }

        // A data-integrity conflict discovered at commit time. Same fail-safe
        // shape as resolution's own ACCOUNT_CONFLICT: terminal, discloses
        // nothing, never retried automatically.
        if (result && result.outcome === 'ACCOUNT_CONFLICT') {
          this.mobileState = 'ACCOUNT_CONFLICT'
          return
        }

        this.mobileError = this.$t('login.mobile.error.generic')
      } catch (err) {
        // Controlled validation failures (422) keep the user on the form with
        // the SAME grant, which the backend still considers recoverable. Only
        // an invalid/expired grant (401) or the rollout gate (404) sends them
        // back to the start, because there is nothing left to retry against.
        this.mobileError = this.mapMobileError(err, 'register')
        if (err && (err.status === 401 || err.status === 404)) {
          this.returnToPhoneEntry()
        }
      } finally {
        this.isRegistering = false
      }
    },

    // TASK-SF-07: the terminal call for the redesigned student journey. Same
    // sequence as submitMobileRegistration's student branch --
    // registerMobileStudent -> fetchUser -> setActiveRole -> /dashboard -- with
    // the wizard's academic / geographic slugs added to the payload
    // (AuthService's allowlist drops any that are blank). The verified
    // registrationGrantId is the only identity authority; no phone / password /
    // userId is ever built into the body.
    async submitStudentOnboarding(values) {
      if (this.isRegistering) return
      this.isRegistering = true
      this.mobileError = null
      this.onboardingEmailError = null
      try {
        const payload = {
          registrationGrantId: this.registrationGrantId,
          firstName: (values.firstName || '').trim(),
          lastName: (values.lastName || '').trim(),
          terms: true,
          email: (values.email || '').trim(),
          educationStage: values.educationStage,
          educationGrade: values.educationGrade,
          studyField: values.studyField,
          schoolName: (values.schoolName || '').trim(),
        }

        const result = await AuthService.registerMobileStudent(payload)

        if (result && result.authenticated) {
          clearOnboardingDraft()
          this.pendingOnboardingValues = null
          await this.$store.dispatch('user/fetchUser')
          await this.$store.dispatch('user/setActiveRole', 'student')
          this.$router.push(`/${i18n.locale}/dashboard`)
          return
        }

        // A commit-time data-integrity conflict: same terminal, disclose-
        // nothing shape as resolution's own ACCOUNT_CONFLICT.
        if (result && result.outcome === 'ACCOUNT_CONFLICT') {
          clearOnboardingDraft()
          this.pendingOnboardingValues = null
          this.mobileState = 'ACCOUNT_CONFLICT'
          return
        }

        this.mobileError = this.$t('login.mobile.error.generic')
      } catch (err) {
        // errorFromHttpResponse only lifts `.status` onto the error when the
        // backend body is `{ err }`; a plain Axios rejection keeps it on
        // `err.response.status`. Read both so the terminal-state branches below
        // fire on a real HTTP response, not just the normalised shape. This is
        // detection only -- grant semantics, ordering and mapMobileError are
        // unchanged.
        const status =
          (err && err.status) ||
          (err && err.response && err.response.status) ||
          null
        const bodyMessage =
          (err &&
            err.response &&
            err.response.data &&
            (err.response.data.message || err.response.data.err)) ||
          ''
        // Prefer the backend's own message; a bare Axios error's `.message`
        // ("Request failed with status code 422") never carries the reason.
        const conflictMessage = bodyMessage || (err && err.message) || ''

        // EMAIL_CONFLICT (an optional email collided at the very end): the
        // wizard must NOT unwind -- hold on step 3, map the message to the
        // email field, and let the user drop the email and retry on the SAME
        // grant (02_* §3.9).
        if (
          (status === 422 || status === 409) &&
          /email/i.test(conflictMessage)
        ) {
          // HUMAN DECISION 5: the EMAIL_CONFLICT state/flow is unchanged
          // (hold on step 3, map to the email field, offer "continue
          // without email"); only the student-facing copy is localised --
          // the raw English server string is never shown in the fa UI.
          this.onboardingEmailError = this.$t(
            'login.studentOnboarding.step3.emailConflict'
          )
          return
        }

        // An invalid / expired grant (401) or the rollout gate (404): there is
        // nothing left to retry against, so re-verify. The typed values ride
        // along in pendingOnboardingValues and are re-keyed to the fresh grant
        // by resolveMobileIdentity (02_* §3.8). Any other 4xx keeps the user on
        // the wizard with the same grant.
        this.mobileError = this.mapMobileError(err, 'register')
        if (status === 401 || status === 404) {
          this.pendingOnboardingValues = { ...values }
          await this.recoverExpiredGrantIntoOtp()
        }
      } finally {
        this.isRegistering = false
      }
    },

    // 02_* §3.8: a grant that expired mid-wizard drops to OTP RE-ENTRY with the
    // phone kept and a fresh code sent -- never back to PHONE_ENTRY. No wizard
    // value is lost: pendingOnboardingValues carries them across.
    async recoverExpiredGrantIntoOtp() {
      this.otpCode = ''
      this.challengeId = null
      this.ownershipGrantId = null
      this.registrationGrantId = null
      this.mobileState = 'OTP_ENTRY'
      try {
        const { challengeId } = await AuthService.sendMobileChallenge(
          this.phoneInputInfo.e164 || this.phone
        )
        this.challengeId = challengeId
        this.startResendCooldown()
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'send')
        this.returnToPhoneEntry()
      }
    },

    // Task 11 / STEP-12 (Q-011): attach the requested role to the account the
    // user is ALREADY signed into. On failure the session survives untouched
    // and only the role attachment is declined -- ROLE_ONBOARDING_UNSUPPORTED
    // is the fail-safe branch, wired up here for the first time.
    async submitRoleOnboarding() {
      if (this.isOnboardingRole) return
      this.isOnboardingRole = true
      this.mobileError = null
      try {
        const payload = await this.$store.dispatch('user/onboardRole', {
          role: this.roleIntent,
          data: { terms: this.roleOnboardingTerms },
        })

        const attached =
          payload.outcome === 'ROLE_ATTACHED' ||
          payload.outcome === 'ROLE_COMPLETED' ||
          payload.outcome === 'ROLE_ALREADY_HELD'

        if (attached) {
          // Reconcile with the server BEFORE navigating. The dashboard guard
          // reads activeRole the moment this push lands, so it must not be
          // allowed to run against an optimistic local value.
          await this.$store.dispatch(
            'user/reconcileAfterRoleOnboarding',
            this.roleIntent
          )
          this.$router.push(`/${i18n.locale}/dashboard`)
          return
        }
        this.mobileState = 'ROLE_ONBOARDING_UNSUPPORTED'
      } catch (err) {
        this.mobileError = this.mapMobileError(err, 'roleOnboarding')
      } finally {
        this.isOnboardingRole = false
      }
    },

    // Maps every backend response into the small controlled set from the
    // STEP-08 authorization -- never a raw backend error body, and a 404
    // (rollout gate off) is always "feature unavailable", never treated as
    // an account-existence signal (SR-09).
    mapMobileError(err, stage) {
      const status = err && err.status
      if (status === 404) {
        return this.$t('login.mobile.error.unavailable')
      }
      // Task 12 / STEP-03 (section 5). Every mobile auth route is rate
      // limited, and a 429 previously fell through to the generic "please try
      // again" -- which tells a rate-limited user to do the one thing that
      // makes their situation worse. Stage-independent on purpose: the meaning
      // is the same wherever the limiter fires. No wait duration is claimed,
      // because the backend sends no Retry-After to claim one from.
      if (status === 429) {
        return this.$t('login.mobile.error.tooManyRequests')
      }
      if (stage === 'send' && status === 422) {
        return this.$t('login.mobile.error.invalidPhone')
      }
      if (
        (stage === 'confirm' || stage === 'login' || stage === 'link') &&
        status === 401
      ) {
        return this.$t('login.mobile.error.invalidOtp')
      }
      // AUTH-G1 (02_* §15.4): a duplicate-email race at confirm time --
      // same shape as the mobile registration path's own EMAIL_CONFLICT.
      if (stage === 'googleRegister' && status === 422) {
        return this.$t('login.mobile.google.error.GOOGLE_REGISTRATION_FAILED')
      }
      return this.$t('login.mobile.error.generic')
    },
  },
}
</script>

<style lang="scss" scoped>
// ---------------------------------------------------------------------------
// Task 12 / STEP-06 — auth card composition (notes E, F, G)
//
// Scoped overrides of the SHARED `.uc-form` rules in scss/layout/_form.scss.
// Deliberately scoped rather than edited globally: `.uc-form` is also the
// signup, verification, reset-password and partner-signup shell, and none of
// those were reviewed in this task. LoginView's own root carries the class, so
// the scope attribute confines every rule below to this view.
// ---------------------------------------------------------------------------
.uc-form {
  // F. The card sat flat on the illustrated desktop background with no border
  // or shadow, so it read as pasted on rather than placed. A soft shadow in
  // the existing blue-grey family lifts it without introducing a new theme.
  box-shadow: 0 4px 24px rgba(52, 52, 64, 0.08);
  padding: 32px 28px;

  // E. The shared rule forces `min-height: 500px` on every state. The short
  // states (pending, conflict, declined role) do not have 500px of content, so
  // the card grew a block of empty white below the action instead of ending.
  // Letting it size to its content is what makes each state look deliberate.
  min-height: 0;

  @include breakpoint-below('small') {
    padding: 24px 20px;
  }

  @include breakpoint-below('tiny') {
    // The shared rule pads 100px here. That is clearance for the FIXED footer
    // (min-height 100px below 'tiny'), so it cannot simply be removed -- the
    // footer would sit on top of the last control. Kept as clearance only,
    // trimmed to the footer's actual height plus a small gap.
    padding: 20px 16px;
    padding-bottom: 108px;

    // Below 'tiny' the card loses its radius and goes edge to edge -- it IS
    // the page surface here, not a card floating on a background. Dropping
    // `min-height` therefore left a bare strip of page backdrop between the
    // end of a short state and the fixed footer. Reaching the footer keeps the
    // surface continuous; the content still sizes itself, so nothing is
    // padded out to fill it.
    //
    // 92px is the logo block above the card: a 130px-wide render of a 1280x618
    // asset is ~63px tall, plus its 20px/8px padding.
    min-height: calc(100vh - 92px);
  }
}

.alert {
  margin: 25px 0;
}

// G. The heading is the anchor of every state; the shared 26px is heavy beside
// the compact mobile card and wrapped the longer role headings onto three
// lines. Slightly tighter, with real line-height so wrapped headings stay
// readable.
.uc-form-header {
  font-size: 22px;
  line-height: 1.45;

  @include breakpoint-above('small') {
    font-size: 25px;
  }
}

.uc-form-text {
  text-align: center;
}

.uc-link.subtext {
  margin: 2px 0 0 10px;
}

// ---------------------------------------------------------------------------
// Task 12 / STEP-02 — visual foundation
//
// One card, one visual language, one action hierarchy. Every state follows the
// same vertical order: heading -> lead copy -> content -> error -> primary CTA
// -> secondary action. These rules exist to make that order look deliberate;
// none of them changes what any state does.
// ---------------------------------------------------------------------------

// Explanatory copy directly under the heading. `.uc-form-text` was doing this
// job with a 25px top margin meant for footer links, which left a gap between
// the heading and the sentence explaining it.
.uc-form-lead {
  color: $c-secondary-grey;
  font-size: 15px;
  line-height: 1.6;
  margin: 8px 0 0 0;
}

// The phone number, wherever it is shown back to the user. A digit run is not
// RTL text: see the RTL block at the end of this file.
.phone-inline {
  color: $c-soft-black;
  font-weight: 500;
}

p.phone-inline {
  margin: 2px 0 0 0;
}

// Secondary actions are subordinate to the primary CTA: centred, smaller, and
// never competing for the same visual weight.
.uc-form-secondary-action {
  font-size: 14px;
  margin: 16px 0 0 0;
  text-align: center;
}

// Terms and similar consent rows. Previously `.uc-form-text`, whose 25px top
// margin and block layout left the checkbox floating away from its own label.
// `gap` is deliberately avoided in this file: flexbox gap is unsupported on
// iOS Safari below 14.1, and this is the mobile-first entry point of the
// product. Margins are used instead so the layout cannot silently collapse on
// an older phone.
.uc-form-checkbox {
  align-items: flex-start;
  display: flex;
  font-size: 14px;
  line-height: 1.5;
  margin: 20px 0 0 0;

  input[type='checkbox'] {
    flex: 0 0 auto;
    height: 18px;
    // Optically aligns the box with the first line of a wrapping label.
    margin: 2px 8px 0 0;
    width: 18px;
  }
}

html.is-rtl .uc-form-checkbox input[type='checkbox'] {
  margin: 2px 0 0 8px;
}

.uc-form-pending {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 40px 0;
  text-align: center;
}

.uc-form-spinner {
  animation: uc-form-spin 0.8s linear infinite;
  border: 3px solid $c-border-grey;
  border-radius: 50%;
  border-top-color: $c-information-blue;
  display: block;
  height: 32px;
  width: 32px;
}

@keyframes uc-form-spin {
  to {
    transform: rotate(360deg);
  }
}

@media (prefers-reduced-motion: reduce) {
  .uc-form-spinner {
    animation-duration: 2.4s;
  }
}

// ---------------------------------------------------------------------------
// THE shared Borna auth card — WP-04V-B round 2.
//
// The rendered prototype's `.auth-card` is ONE card, identical on every auth
// screen: 460px wide, 32px radius, 36px padding, a 1px #e2e8f0 hairline, a
// 96%-opaque white fill and a 0 18px 50px shadow; 346px / 25px / 25px 20px at
// 390px. Before round 2 those five values were restated inside
// `.uc-form--design-01` and (partly, and differently) inside
// `.uc-form--design-02`, and not at all for role selection, registration,
// account conflict or role onboarding -- which is why those screens kept the
// legacy 500px / 22px / 32px 28px card. They are declared once, here.
// ---------------------------------------------------------------------------
.uc-form--borna-auth {
  background: $borna-auth-card-bg;
  border: 1px solid $borna-border;
  border-radius: $borna-radius-auth;
  box-shadow: $borna-shadow-auth-card;
  margin-left: auto;
  margin-right: auto;
  max-width: $borna-auth-card-width;
  min-height: 0;
  padding: $borna-auth-card-pad;

  @include breakpoint-below('small') {
    border-radius: $borna-radius-auth-mobile;
    padding: $borna-auth-card-pad-mobile;
  }

  // The prototype keeps the auth card as a distinct rounded card on mobile.
  // The shared `.uc-form` rule forces `border-radius: 0` and a 100px fixed-
  // footer clearance below 'tiny'; this shell has no fixed footer and must
  // stay a card, so both are overridden here (page-side spacing comes from
  // FormPageTemplate's `.FormPageTemplate` padding).
  @include breakpoint-below('tiny') {
    border-radius: $borna-radius-auth-mobile;
    min-height: 0;
    padding: $borna-auth-card-pad-mobile;
  }

  // The session-expired / auth-error notice, restyled onto the canonical
  // surface. It stays exactly the same notice with exactly the same trigger;
  // the prototype simply never draws this state, so it follows the shell's own
  // radius and type rather than Bootstrap's 4px alert.
  .alert.alert-danger {
    background: $borna-error-bg;
    border: 1px solid $borna-error-border;
    border-radius: $borna-radius-md;
    color: $borna-error-text;
    font-size: 13px;
    line-height: 1.7;
    margin: 16px 0 0;
    padding: 12px 14px;
    text-align: center;
  }

  // Lead copy: prototype `.auth-copy p`, 14px / 1.9 leading, muted.
  .uc-form-lead {
    color: $borna-muted;
    font-size: 14px;
    line-height: 1.9;
  }

  // Secondary actions inherit the shell's muted ink rather than the legacy
  // grey, so a "back" link reads the same on every screen of the journey.
  .uc-form-secondary-action {
    color: $borna-muted;
    font-size: 13px;
  }

  // Text fields. The journey's canonical field is the one measured on the
  // prototype's phone input (`.design01-phone`): 54px tall, 16px radius, a
  // plain #e2e8f0 hairline on white, indigo on focus. The registration-form
  // and Google-account states were still drawing the legacy `.uc-form-input`
  // (no box, a 3px teal underline) beside it on the same shell, so the same
  // treatment is applied once here.
  //
  // Deliberately NOT `input`: checkboxes and radios are untouched, the OTP
  // boxes own their own `::v-deep` rules, and the phone field is excluded by
  // name -- it is the INNER control of `.design01-phone`, which already draws
  // the box around it, so styling it as a field too double-boxed the landing
  // screen's only input.
  input[type='text']:not(.design01-phone__field),
  input[type='email']:not(.design01-phone__field),
  input[type='tel']:not(.design01-phone__field),
  input[type='password']:not(.design01-phone__field) {
    background: $borna-surface;
    border: 1px solid $borna-border;
    border-bottom: 1px solid $borna-border;
    border-radius: $borna-radius-card;
    box-shadow: none;
    box-sizing: border-box;
    height: 54px;
    padding: 0 15px;
    width: 100%;

    &::placeholder {
      color: $borna-muted-light;
    }

    &:focus {
      border-color: $borna-indigo-300;
      outline: none;
    }
  }

  .uc-form-label {
    color: $borna-text;
    font-size: 13px;
    font-weight: $borna-fw-bold;
    margin: 0 2px 8px;
  }
}

// ---------------------------------------------------------------------------
// DESIGN-01 — login landing composition
//
// Every rule here is scoped to this view and (via `.uc-form--design-01` /
// element classes only used at PHONE_ENTRY) never reaches DESIGN-02/03 or the
// email-fallback screen. Colours/spacing come from the existing token set.
// ---------------------------------------------------------------------------
.uc-form--design-01 {
  // Card geometry (460 / 32 / 36 / hairline / 96% white / 0 18px 50px) is
  // declared once on `.uc-form--borna-auth` above and is no longer restated.
  text-align: center;

  // Prototype `.auth-start-title`: measured 22px/400 at 1440 and 15.6px at 390
  // -- the prototype's headings are FLUID (`clamp()`), not stepped, which is
  // why the old `breakpoint-below('tiny')` step to 16px could never line up.
  // `$borna-fs-h1-login` reproduces both measured endpoints. Weight now comes
  // from the WP-04V-A heading reset (400), not Bootstrap Reboot's 500.
  .uc-form-header {
    font-size: $borna-fs-h1-login;
    letter-spacing: -0.035em;
    line-height: 1.55;
    margin: 9px 0 12px;
  }

  // Prototype `.auth-copy` lead: measured 14px/1.9, muted.
  .uc-form-lead {
    color: $borna-muted;
    font-size: 14px;
    line-height: 1.9;
    margin: 0 auto 28px;
    max-width: 34rem;
  }

  .uc-form-element {
    text-align: start;
  }

  // WP-04V-B: the prototype's `.field-label` measures 13px/700 in the ink
  // colour with 8px below; this shipped as the legacy 12px/400 grey.
  // Scoped inside `.uc-form--design-01` so every other form keeps its label.
  .uc-form-label {
    color: $borna-text;
    font-size: 13px;
    font-weight: $borna-fw-bold;
    margin: 0 2px 8px;
  }
}

.design01-required-star {
  color: #d14545; // measured; not part of the shared palette
  font-weight: $borna-fw-black;
  margin-inline-start: 2px;
}

// ---------------------------------------------------------------------------
// TASK-SF-07 — student onboarding card geometry.
//
// The card class is present ONLY at `mobileState === 'STUDENT_ONBOARDING'`
// (see `isStudentOnboardingState`), reached from the student role card
// alone. Mirrors the prototype `.auth-card` (min(100%, 460px), 32px radius,
// generous padding) and, like DESIGN-01, overrides the shared `.uc-form`
// mobile rule so the card stays a distinct rounded card with no fixed-footer
// clearance. All wizard-internal styling lives in the non-scoped
// `_student-onboarding.scss` partial nested under `.student-onboarding-wizard`.
// ---------------------------------------------------------------------------
.uc-form--student-onboarding {
  // Card geometry is declared once on `.uc-form--borna-auth` above; the wizard
  // keeps only its own slightly tighter bottom padding and start alignment.
  // The mobile padding is restated because a media query adds no specificity:
  // without it this desktop value would also win below 'small'.
  padding: 32px 32px 28px;
  text-align: start;

  @include breakpoint-below('small') {
    padding: $borna-auth-card-pad-mobile;
  }
}

// Brand lockup: [Borna | divider | «محصولی از» + foundation]. DOM order puts
// Borna first, so it lands on the visual right in the RTL (fa) card. Sizing
// tracks the prototype `.auth-brand-pair` (~340px wide, Borna mark the larger
// of the two).
.design01-lockup {
  align-items: center;
  display: flex;
  justify-content: center;
  margin: 0 auto 20px;
  max-width: 340px;
}

// The Borna side of the landing lockup: artwork over its tagline line.
.design01-lockup__mark {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

.design01-lockup__borna {
  display: block;
  height: 84px;
  object-fit: contain;
  width: auto;

  // Measured 88x74 at 390px -- the prototype scales the mark down on mobile.
  @include breakpoint-below('tiny') {
    height: 74px;
  }
}

.design01-lockup__divider {
  align-self: center;
  background: $c-border-grey;
  flex: 0 0 1px;
  height: 62px;
  margin: 0 18px;
  width: 1px;
}

.design01-lockup__foundation {
  align-items: center;
  display: flex;
  flex-direction: column;
  justify-content: center;
}

// WP-04V-B: measured 8px/800 muted in the prototype (was 11px/400 legacy grey).
.design01-lockup__powered-by {
  color: $borna-muted;
  font-size: 8px;
  font-weight: $borna-fw-extrabold;
  margin-bottom: 4px;
}

.design01-lockup__foundation-mark {
  display: block;
  height: 58px;
  object-fit: contain;
  width: auto;
}

// WP-04V-B: measured 12px/800 #314bd6 in the prototype (was 13px/500 and the
// legacy information-blue). The hairline above it is kept -- the prototype has
// the same rule on the brand block's bottom edge rather than the eyebrow's top,
// which renders identically.
.design01-eyebrow {
  align-items: center;
  border-top: 1px solid $borna-border;
  color: $borna-indigo-600;
  display: flex;
  font-size: $borna-fs-chip + 2px;
  font-weight: $borna-fw-extrabold;
  justify-content: center;
  margin: 0;
  padding-top: 16px;
}

.design01-eyebrow__spark {
  flex: 0 0 auto;
  margin-inline-end: 6px;
}

// --- Fixed +۹۸ phone field ----------------------------------------------
// WP-04V-B: the prototype's `.phone-field` measures 54px tall with a 16px
// radius, a plain #e2e8f0 hairline and a white fill -- the prefix is inline
// muted text, NOT a filled chip with its own divider as this shipped.
.design01-phone {
  align-items: stretch;
  background: $borna-surface;
  border: 1px solid $borna-border;
  border-radius: $borna-radius-card;
  display: flex;
  height: 54px;
  margin-top: 6px;
  overflow: hidden;
  padding: 0 15px;

  &:focus-within {
    border-color: $borna-indigo-300;
    outline: none;
  }
}

.design01-phone__prefix {
  align-items: center;
  color: $borna-muted;
  display: flex;
  flex: 0 0 auto;
  font-weight: $borna-fw-regular;
  padding-inline-end: 12px;
}

.design01-phone__field {
  background: transparent;
  border: 0;
  flex: 1 1 auto;
  font-size: 16px;
  min-width: 0;
  outline: none;
  padding: 0;

  &::placeholder {
    color: $borna-muted-light;
  }
}

// --- Primary CTA ---------------------------------------------------------
// Prototype `.button`/`.button--primary`: a rounded rectangle (14px), not the
// legacy pill. Scoped to DESIGN-01 -- the shared `.uc-form-button` (200px) is
// untouched. Height (48px), colours, disabled and loading states all come from
// the shared rule unchanged.
// WP-04V-B: measured 48px tall, 14px radius, 12px/800, 18px above, 0 19px
// inline padding -- and NO shadow. The 0 8px 20px glow this shipped with is not
// in the rendered prototype.
.design01-cta {
  align-items: center;
  // The shared `.uc-form-button` paints the legacy information-blue (#1855d1);
  // the prototype's primary is the Borna indigo #314bd6. Scoped here so every
  // other form button keeps the legacy colour.
  background-color: $borna-indigo-600;
  border-radius: $borna-radius-nav;
  color: $borna-surface;
  display: flex;
  font-size: $borna-fs-chip + 2px;
  font-weight: $borna-fw-extrabold;
  justify-content: center;
  margin-top: 18px;
  padding: 0 19px;

  // The prototype renders no hover or disabled state, so both are expressed in
  // its own palette rather than left on the legacy greys (the same licence
  // WP-03 took for the chat's processing/error treatments, doc 11 D1-D4).
  &:hover:not(:disabled) {
    background-color: $borna-indigo-900;
  }

  &:disabled {
    background-color: $borna-canvas;
    color: $borna-muted-light;
  }
}

.design01-cta__arrow {
  flex: 0 0 auto;
  margin-inline-start: 8px;
}

// --- «یا» divider ---------------------------------------------------------
.design01-divider {
  align-items: center;
  color: $borna-muted;
  display: flex;
  font-size: $borna-fs-marker; // measured 9px, was 13px
  margin: 13px 0;

  &::before,
  &::after {
    background: $borna-border;
    content: '';
    flex: 1 1 auto;
    height: 1px;
  }

  // WP-04V-B: measured -- the prototype's «یا» is the glyph alone (5px wide),
  // the rules meeting it on both sides, not a 12px-padded gap.
  span {
    padding: 0;
  }
}

// --- Google entry point ---------------------------------------------------
// Prototype `.google-login`: 13px rounded rectangle, 44px tall. Scoped to
// DESIGN-01 -- the shared `.uc-form-button.google` (200px / 48px) is untouched.
// Border, background, hover and disabled states come from the shared rule.
// WP-04V-B: measured 44px tall, 13px radius, 14px/800 #071447 on white with a
// 1px #e2e8f0 hairline.
.design01-google {
  align-items: center;
  border: 1px solid $borna-border;
  border-radius: 13px;
  color: $borna-ink;
  display: flex;
  font-size: 14px;
  font-weight: $borna-fw-extrabold;
  height: 44px;
  justify-content: center;
  margin-top: 0;
}

.design01-google__icon {
  flex: 0 0 auto;
  height: 22px;
  margin-inline-end: 10px;
  width: 22px;
}

// --- Security note -------------------------------------------------------
// WP-04V-B: the prototype's `.trust-line` measures 11px/400 muted, 18px above,
// with a teal (not green) shield.
.design01-secure {
  align-items: center;
  color: $borna-muted;
  display: flex;
  font-size: 11px;
  justify-content: center;
  line-height: 1.75;
  margin: 18px 0 0;
}

.design01-secure__icon {
  color: $borna-teal-700;
  flex: 0 0 auto;
  margin-inline-end: 6px;
}

// --- Registration form -----------------------------------------------------
.uc-form-required {
  color: $c-secondary-grey;
  font-size: 12px;
  font-weight: 400;
  margin-left: 4px;
}

html.is-rtl .uc-form-required {
  margin-left: 0;
  margin-right: 4px;
}

// ---------------------------------------------------------------------------
// DESIGN-03 — role selection / account creation — WP-04V-B round 2.
//
// This is the screen the human's round-1 visual UAT rejected. It rendered the
// legacy composition entirely: the illustrated UpChieve page, `header_logo.jpg`
// above a 500px/22px card, IRANSans 25/500 headings and the 3-link grey footer
// bar. The shell now comes from `.uc-form--borna-auth`; what remains here is
// this screen's own composition, measured from the rendered prototype.
// ---------------------------------------------------------------------------
.uc-form--design-03 {
  text-align: center;

  // Prototype role-selection `h1`: measured 24px at 1440 and 19.89px at 390
  // (fluid, like every other prototype heading), 1.6 leading, ink, tight
  // tracking. This shipped as the shared 25px/500 in the legacy ink colour.
  .uc-form-header {
    font-size: $borna-fs-h1-auth-role;
    letter-spacing: -0.02em;
    line-height: 1.6;
    margin: 6px 0 8px;
  }

  // Prototype lead: 14px / 1.9, muted, no bottom margin -- the choice group
  // owns the gap below it.
  .uc-form-lead {
    margin: 0;
  }
}

// --- Role choice -----------------------------------------------------------
// Prototype `.role-entry`: a 20px card, 1px #e2e8f0, white, 15px padding, laid
// out as [54px icon tile | content | 30px arrow tile] with a 13px gutter. The
// two roles carry different tints (indigo / teal) and stay equal weight --
// this screen has no separate primary CTA, the choice IS the action.
.role-choice-group {
  margin-top: 20px;
}

.role-choice {
  align-items: center;
  background: $borna-surface;
  border: 1px solid $borna-border;
  border-radius: $borna-radius-role-card;
  cursor: pointer;
  display: grid;
  grid-template-columns: 54px 1fr 30px;
  // `gap` is safe here: this screen is Borna-only and the shell it sits on
  // already requires a grid-capable browser.
  gap: 13px;
  padding: 15px;
  text-align: start;
  transition: border-color 0.15s ease, background-color 0.15s ease;
  width: 100%;

  & + & {
    margin-top: 11px;
  }

  &:hover {
    border-color: $borna-indigo-300;
  }

  &:focus-visible {
    outline: 2px solid $borna-indigo-600;
    outline-offset: 2px;
  }
}

// 54x54 tinted tile, 17px radius. Indigo for the student route, teal for the
// volunteer route, exactly as the prototype tints them.
.role-choice__icon {
  align-items: center;
  border-radius: $borna-radius-role-icon;
  display: flex;
  height: 54px;
  justify-content: center;
  width: 54px;
}

// 30x30, 10px radius, filled with the page canvas colour.
.role-choice__arrow {
  align-items: center;
  background: $borna-canvas;
  border-radius: $borna-radius-role-arrow;
  display: flex;
  height: 30px;
  justify-content: center;
  width: 30px;
}

.role-choice--student {
  .role-choice__icon {
    background: $borna-indigo-50;
    color: $borna-indigo-600;
  }

  .role-choice__arrow {
    color: $borna-indigo-600;
  }
}

.role-choice--volunteer {
  .role-choice__icon {
    background: $borna-teal-50;
    color: $borna-teal-700;
  }

  .role-choice__arrow {
    color: $borna-teal-700;
  }
}

// P42-04: the teacher card while teacher registration is Coming Soon. Same
// size and place, dashed outline, muted tile and copy -- the same disabled
// vocabulary as the Reports filter (P40 QA-32). `-webkit-text-fill-color`
// and `opacity: 1` keep iOS from dimming the copy a second time.
.role-choice--disabled,
.role-choice:disabled {
  background: $borna-canvas;
  border-style: dashed;
  cursor: not-allowed;
  opacity: 1;

  &:hover {
    border-color: $borna-border;
  }

  .role-choice__label,
  .role-choice__hint {
    color: $borna-muted;
    -webkit-text-fill-color: currentColor;
  }
}

.role-choice--volunteer.role-choice--disabled {
  .role-choice__icon {
    background: $borna-canvas;
    color: $borna-muted;
  }

  .role-choice__arrow {
    background: transparent;
    color: $borna-muted-light;
  }
}

.role-choice__content {
  display: block;
  min-width: 0;
}

.role-choice__label {
  color: $borna-ink;
  display: block;
  font-size: 15px;
  font-weight: $borna-fw-bold;
  line-height: 1.75;
}

.role-choice__hint {
  color: $borna-muted;
  display: block;
  font-size: 11px;
  line-height: 1.8;
}

// SX-02: the same «به‌زودی» pill the Home cards use (HomeActionCard.vue).
.role-choice__badge {
  display: inline-block;
  margin-inline-start: 8px;
  padding: 2px 9px;
  border-radius: 999px;
  background: #fff7ed;
  color: #9a5b13;
  font-size: 11px;
  font-weight: $borna-fw-bold;
  line-height: 1.6;
  vertical-align: middle;
  white-space: nowrap;
}

// Prototype `.auth-hint`: a 17px shield glyph and a 10px muted line, centred
// under the two choices.
.role-choice-note {
  align-items: center;
  color: $borna-muted;
  display: flex;
  font-size: 10px;
  justify-content: center;
  margin: 18px 0 0;
}

.role-choice-note__icon {
  flex: 0 0 auto;
  margin-inline-end: 6px;
}

// --- OTP resend (shared rhythm) ------------------------------------------
.otp-resend {
  margin-top: 12px;
  text-align: center;
}

.otp-resend__notice {
  color: $c-information-blue;
  font-size: 13px;
  margin: 4px 0 0 0;
  text-align: center;
}

// ---------------------------------------------------------------------------
// DESIGN-02 — OTP verification composition
//
// Scoped to `.uc-form--design-02` (present only at OTP_ENTRY) and to element
// classes used nowhere else, so DESIGN-01, DESIGN-03 and the shared signup /
// verification OTP surfaces (`_otpInput.scss`) are untouched. Palette and
// spacing follow the interactive prototype's DESIGN-02 screen; the six-box
// OTP, the real invalid/error state and the throttle-not-expiry resend
// wording are the intended deviations from that mock.
// ---------------------------------------------------------------------------
.uc-form--design-02 {
  // Card geometry is declared once on `.uc-form--borna-auth` above.
  text-align: center;

  // Prototype `.auth-copy h1`: midnight, tight tracking, ~1.55 leading.
  // WP-04V-B round 2: measured 26px at 1440 AND 21.06px at 390 -- fluid, like
  // every other prototype heading, so the stepped 26/23 pair could match
  // neither endpoint.
  .uc-form-header {
    color: $borna-ink;
    font-size: $borna-fs-h1-auth-otp;
    letter-spacing: -0.035em;
    line-height: 1.55;
    margin: 6px 0 0;
  }

  // Prototype `.auth-copy p`: 14px / 1.9 leading / muted, centred, with the
  // masked number folded in.
  .uc-form-lead {
    margin: 10px auto 20px;
    max-width: 32rem;
  }

  .uc-form-element {
    text-align: center;
  }
}

// Borna wordmark only -- the foundation mark is deliberately NOT shown past
// the landing screen (prototype `.auth-brand-pair--borna-only`), with a
// hairline divider beneath it. Shared by DESIGN-02 and DESIGN-03: the
// prototype composes both screens' brand blocks identically (measured 98x83 at
// 1440, hairline beneath, same 22px gap to the back link).
// WP-04V-B round 2, measured: the hairline block is 180px wide, not 320px,
// its rule is indigo-100 rather than the neutral border token, and it pads
// `2px 8px 14px` with a 25px gap to what follows.
.auth-lockup {
  border-bottom: 1px solid $borna-indigo-100;
  margin: 0 auto 25px;
  max-width: 180px;
  padding: 2px 8px 14px;
}

// The mark's own tagline line. The prototype draws it as TEXT beneath the
// artwork (measured 9px/800 #243ab4, 78x12 at 1440) -- it is not baked into
// the brand image, which is why `borna-identity.png` alone rendered the
// lockup a line short on every in-card auth screen.
.auth-lockup__tagline {
  color: $borna-indigo-900;
  display: block;
  font-size: 9px;
  font-weight: $borna-fw-extrabold;
  line-height: 1.35;
  text-align: center;
}

.auth-lockup__borna {
  display: block;
  height: 83px;
  margin: 0 auto;
  object-fit: contain;
  width: auto;

  // Measured 88x74 at 390px -- the prototype scales the mark down on mobile.
  @include breakpoint-below('tiny') {
    height: 74px;
  }
}

// The "back" affordance at the top of the card -- «اصلاح شماره» on DESIGN-02,
// «بازگشت به ورود» on DESIGN-03. Same handler as the control it replaced;
// position, icon and label only. Measured 12px/400 in `$borna-muted`, sitting
// at the card's start edge.
//
// The prototype's own control is 21px tall. The 44px min-height is kept
// deliberately: 21px is below a usable tap target, and this is the mobile-first
// entry point of the product. The surrounding margins absorb the difference.
.auth-back {
  align-items: center;
  align-self: flex-start;
  background: none;
  border: 0;
  color: $borna-muted;
  cursor: pointer;
  display: inline-flex;
  font: inherit;
  font-size: 12px;
  gap: 6px;
  margin: 0 0 2px;
  min-height: 44px;
  padding: 0;

  &:hover {
    color: $borna-text;
  }

  &:focus-visible {
    border-radius: 6px;
    outline: 2px solid $borna-indigo-600;
    outline-offset: 2px;
  }

  &:disabled {
    color: $c-disabled-grey;
    cursor: default;
  }
}

.auth-back__arrow,
.design02-back__arrow {
  flex: 0 0 auto;
}

// P43-02 (Owner UAT, superseding P42-03): Back is «→» in Persian and «←» in
// English. The arrow is drawn pointing left, so it is shown as drawn in the
// English (LTR) locale and mirrored in the RTL (Persian) locale.
html.is-rtl .auth-back__arrow,
html.is-rtl .design02-back__arrow {
  transform: scaleX(-1);
}

// Prototype `.eyebrow`: indigo, 12px, heavy, slight positive tracking.
.auth-eyebrow {
  color: $borna-indigo-600;
  font-size: 12px;
  font-weight: $borna-fw-extrabold;
  letter-spacing: 0.02em;
  margin: 0 0 2px;
}

// Prototype `.otp-row input`: indigo box, 52x56, 1.5px indigo-300 border,
// indigo-50 fill, 21px/800 indigo-900 digits, 9px gaps. Scoped hard to
// DESIGN-02 so `_otpInput.scss` (signup / verification) is unchanged.
.uc-form--design-02 {
  ::v-deep .simple-otp-input {
    direction: ltr;
    margin-top: 4px;
  }

  ::v-deep .single-input-container {
    // 2 * 4.5px == the prototype's 9px inter-box gap.
    margin: 0 4.5px;
  }

  ::v-deep .otp-single-input {
    background: #eef2ff;
    border: 1.5px solid #91a5ff;
    border-radius: 14px;
    color: #172b85;
    font-size: 21px;
    font-weight: 800;
    height: 56px;
    width: 52px;
  }

  // Visible indigo focus -- the prototype's boxes are read-only and carry
  // none, but a keyboard user typing here needs one.
  ::v-deep .otp-single-input:focus {
    border-color: #314bd6;
    box-shadow: 0 0 0 4px rgba(49, 75, 214, 0.16);
    // Keeps a ring in forced-colors / Windows high-contrast mode.
    outline: 2px solid transparent;
    outline-offset: 2px;
  }

  // Bounded error border on the boxes (allowed by the brief). `aria-invalid`
  // is set on every box via `otpInputProps` whenever `mobileError` is shown.
  ::v-deep .otp-single-input[aria-invalid='true'] {
    background: #fdf2f2;
    border-color: $c-error-red;
  }

  @include breakpoint-below('small') {
    ::v-deep .single-input-container {
      margin: 0 3px;
    }

    ::v-deep .otp-single-input {
      font-size: 19px;
      height: 53px;
      width: 46px;
    }
  }

  @include breakpoint-below('tiny') {
    ::v-deep .otp-single-input {
      font-size: 18px;
      height: 50px;
      width: 42px;
    }
  }
}

// Compact single-line resend. The mm:ss suffix is the LOCAL resend throttle
// only -- deliberately not phrased as a code expiry (see
// RESEND_COOLDOWN_SECONDS / `resendMobileCode`).
.design02-resend {
  margin-top: 16px;
}

.design02-resend__button {
  color: $c-secondary-grey;
  font-size: 13px;

  &:hover:not(:disabled) {
    color: $c-information-blue;
  }
}

.design02-resend__timer {
  // Tabular figures stop the row from reflowing as the countdown ticks.
  font-variant-numeric: tabular-nums;
  white-space: nowrap;
}

.design02-resend__notice {
  margin-top: 8px;
}

// Prototype `.button` / `.button--primary`: a rounded rectangle (14px), not
// the legacy pill, with a soft indigo shadow. Height, disabled and hover
// states come from the shared `.uc-form-button` rule unchanged.
.design02-cta {
  border-radius: 14px;
  box-shadow: 0 8px 20px rgba(49, 75, 214, 0.22);
  font-size: 15px;
  margin-top: 18px;
}

// --- Direction -------------------------------------------------------------
// `html.is-rtl * { direction: rtl }` in App/index.vue applies to every element,
// including phone fields, so a `dir` attribute or a bare `.ltr` class cannot
// win on specificity. Nesting under `html.is-rtl` is the pattern the signup
// forms already use for exactly this, and is what is reused here: the page
// stays RTL while digit runs read left-to-right.
html.is-rtl {
  .uc-link.subtext {
    margin: 2px 10px 0 0;
  }

  .phone-input,
  .phone-inline {
    direction: ltr;
  }

  // _otpInput.scss already sets direction: ltr on .simple-otp-input, but
  // `html.is-rtl *` outranks it on specificity, so it is restated here where
  // it can win.
  ::v-deep .simple-otp-input {
    direction: ltr;
  }

  // The number is shown back to the user as confirmation, so it stays on the
  // reading side of an RTL card rather than jumping to the far edge.
  p.phone-inline {
    text-align: right;
  }

  // DESIGN-01: `.phone-input` above already forces the +۹۸ row to LTR (prefix
  // on the left, digits after), matching the reference. The typed digits still
  // need explicit LTR because `html.is-rtl *` re-asserts rtl on the <input>.
  .design01-phone__field {
    direction: ltr;
    text-align: left;
  }
}

// DESIGN-01: the CTA chevron points in the reading-forward direction. The SVG
// is drawn pointing left (correct for the RTL design locale); flip it for LTR.
html:not(.is-rtl) .design01-cta__arrow {
  transform: scaleX(-1);
}

// P43-02 (Owner UAT): the student role card continues forward: «←» in Persian
// (as drawn), «→» in English. The volunteer card belongs to the teacher journey
// and is left as it is.
html:not(.is-rtl) .role-choice--student .role-choice__arrow svg {
  transform: scaleX(-1);
}
</style>
